import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";

import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading,
  List,
  Link,
  BlockQuote,
  Table,
  TableToolbar,
  Alignment,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";
import {
  FaUsers,
  FaShoppingBag,
  FaDollarSign,
  FaSignOutAlt,
  FaSearch,
  FaCheck,
  FaUserCheck,
  FaStar,
  FaChartLine,
  FaUserAstronaut,
  FaCheckCircle,
  FaBoxes,
  FaPlus,
  FaTrash,
  FaFolderOpen,
  FaFileImage,
  FaEdit,
  FaEnvelope,
  FaBlog,
  FaTag,
} from "react-icons/fa";
import { GiPrayerBeads } from "react-icons/gi";
import API from "../../config";
import {
  getProductOptions,
  createOptionGroup,
  updateOptionGroup,
  deleteOptionGroup,
} from "../../api/productOptionApi";
import { createShipment } from "../../api/orderApi";
import AdminConsultations from "./AdminConsultations";
import AdminAnnouncements from "./AdminAnnouncements";
import AdminCheckoutSettings from "./AdminCheckoutSettings";
import AdminMobileNumerology from "./AdminMobileNumerology";
import AdminRudraksha from "./AdminRudraksha";
import AdminProductFaqs from "./AdminProductFaqs";
import "./AdminDashboard.css";

const initialAstrologers = [
  {
    id: "a1",
    name: "Astrologer Vickey",
    userId: "u2",
    specialization: ["Vedic", "Palmistry"],
    experienceYrs: 8,
    hourlyRate: 500,
    rating: 4.8,
    isApproved: true,
    date: "2026-07-03",
  },
  {
    id: "a2",
    name: "Amit Verma",
    userId: "u4",
    specialization: ["Numerology", "Vastu"],
    experienceYrs: 5,
    hourlyRate: 400,
    rating: 4.5,
    isApproved: false,
    date: "2026-07-01",
  },
  {
    id: "a3",
    name: "Pandit Hari Prasad",
    userId: "u8",
    specialization: ["Kundli", "Gemology"],
    experienceYrs: 15,
    hourlyRate: 1000,
    rating: 4.9,
    isApproved: false,
    date: "2026-06-29",
  },
  {
    id: "a4",
    name: "Sonia Gandhi Astro",
    userId: "u9",
    specialization: ["Tarot Cards", "Face Reading"],
    experienceYrs: 4,
    hourlyRate: 350,
    rating: 4.2,
    isApproved: true,
    date: "2026-06-27",
  },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [adminUser, setAdminUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersPagination, setUsersPagination] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [astrologers, setAstrologers] = useState(initialAstrologers);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersPagination, setOrdersPagination] = useState(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [orderStatusFilter, setOrderStatusFilter] = useState("ALL");
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [shippingOrderId, setShippingOrderId] = useState(null);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactsPagination, setContactsPagination] = useState(null);
  const [contactsPage, setContactsPage] = useState(1);
  const [expandedContactId, setExpandedContactId] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(false);
  const [blogsPagination, setBlogsPagination] = useState(null);
  const [blogsPage, setBlogsPage] = useState(1);
  const [blogSearchTerm, setBlogSearchTerm] = useState("");
  const [blogStatusFilter, setBlogStatusFilter] = useState("ALL");
  const emptyBlogForm = {
    title: "",
    slug: "",
    category: "",
    image: "",
    excerpt: "",
    content: "",
    author: "",
    status: "draft",
  };
  const [blogForm, setBlogForm] = useState(emptyBlogForm);
  const [editingBlog, setEditingBlog] = useState(null);
  const [savingBlog, setSavingBlog] = useState(false);
  const [vaidikSubmissions, setVaidikSubmissions] = useState([]);
  const [loadingVaidik, setLoadingVaidik] = useState(false);
  const [vaidikPagination, setVaidikPagination] = useState(null);
  const [vaidikPage, setVaidikPage] = useState(1);
  const [podSubmissions, setPodSubmissions] = useState([]);
  const [loadingPod, setLoadingPod] = useState(false);
  const [podPagination, setPodPagination] = useState(null);
  const [podPage, setPodPage] = useState(1);
  const [podSearchTerm, setPodSearchTerm] = useState("");
  const [podStatusFilter, setPodStatusFilter] = useState("ALL");
  const [selectedPodModal, setSelectedPodModal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [productsTotal, setProductsTotal] = useState(0);
  // Inventory table: server-side search + pagination, separate from
  // productsList (which the coupon product picker needs in full)
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [inventoryPagination, setInventoryPagination] = useState(null);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventorySearch, setInventorySearch] = useState("");
  const [inventoryStatusFilter, setInventoryStatusFilter] = useState("");
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventoryRefreshKey, setInventoryRefreshKey] = useState(0);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [catalogSubTab, setCatalogSubTab] = useState("products");
  const [catName, setCatName] = useState("");
  const [catSlug, setCatSlug] = useState("");
  const [catDescription, setCatDescription] = useState("");
  const [catParentId, setCatParentId] = useState("");
  // Subcategory image: newly picked file, its preview, the saved image (when editing) and a "remove" flag
  // Which dedicated form is open: null (closed) | "category" | "subcategory"
  const [catFormMode, setCatFormMode] = useState(null);
  const [catSearch, setCatSearch] = useState("");
  const [catImageFile, setCatImageFile] = useState(null);
  const [catImagePreview, setCatImagePreview] = useState("");
  const [catExistingImage, setCatExistingImage] = useState("");
  const [catRemoveImage, setCatRemoveImage] = useState(false);
  // null = creating; otherwise { type: "category" | "subcategory", id }
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductForm, setEditProductForm] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [prodCategoryId, setProdCategoryId] = useState("");
  const [prodSubcategoryId, setProdSubcategoryId] = useState("");
  const [prodName, setProdName] = useState("");
  const [prodSlug, setProdSlug] = useState("");
  const [prodDescription, setProdDescription] = useState("");
  const [prodPrice, setProdPrice] = useState("");
  const [prodMukhiCount, setProdMukhiCount] = useState("");
  const [prodOrigin, setProdOrigin] = useState("");
  const [prodIsCertified, setProdIsCertified] = useState(false);
  const [prodCertificationUrl, setProdCertificationUrl] = useState("");
  const [prodBeadSizeMm, setProdBeadSizeMm] = useState("");
  const [prodStockQuantity, setProdStockQuantity] = useState("");
  const [prodStatus, setProdStatus] = useState("draft");
  const [prodImages, setProdImages] = useState([]);
  const [optionsPanelProduct, setOptionsPanelProduct] = useState(null);
  const [faqPanelProduct, setFaqPanelProduct] = useState(null);
  const [productOptionGroups, setProductOptionGroups] = useState([]);
  const [loadingProductOptions, setLoadingProductOptions] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const emptyGroupForm = {
    id: null,
    name: "",
    selectionType: "single",
    isRequired: false,
    showWhenGroup: "",
    showWhenPath: "",
    choices: [{ label: "", priceDelta: "", imageUrl: "" }],
  };
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponsPagination, setCouponsPagination] = useState(null);
  const [couponsPage, setCouponsPage] = useState(1);
  const [couponSearchTerm, setCouponSearchTerm] = useState("");
  const [couponStatusFilter, setCouponStatusFilter] = useState("ALL");
  const emptyCouponForm = {
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    maxDiscountAmount: "",
    minOrderAmount: "",
    applicability: "all",
    categoryIds: [],
    productIds: [],
    isSingleUse: false,
    requiresLogin: false,
    isActive: true,
    expiresAt: "",
  };
  const [couponForm, setCouponForm] = useState(emptyCouponForm);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsPagination, setReviewsPagination] = useState(null);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewSearchTerm, setReviewSearchTerm] = useState("");
  const [reviewRatingFilter, setReviewRatingFilter] = useState("ALL");
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const userStr = localStorage.getItem("adminUser");

    if (!token || !userStr) {
      navigate("/admin/login");
      return;
    }
    const user = JSON.parse(userStr);
    if (user.role !== "ADMIN") {
      localStorage.clear();
      navigate("/admin/login");
    } else {
      setAdminUser(user);
    }
  }, [navigate]);

  useEffect(() => {
    if (
      adminUser &&
      (activeTab === "catalog" ||
        activeTab === "dashboard" ||
        activeTab === "coupons")
    ) {
      fetchCategories();
      fetchProducts();
    }
  }, [activeTab, adminUser]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "users" && activeTab !== "dashboard") return;

    const timeout = setTimeout(() => {
      fetchUsers();
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, searchTerm, roleFilter, usersPage]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "orders" && activeTab !== "dashboard") return;

    const timeout = setTimeout(() => {
      fetchOrders();
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, searchTerm, orderStatusFilter, ordersPage]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "contact") return;
    fetchContactSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, contactsPage]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "blog") return;

    const timeout = setTimeout(() => {
      fetchBlogs();
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, blogSearchTerm, blogStatusFilter, blogsPage]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "vaidikvidhya") return;
    fetchVaidikSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, vaidikPage]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "reviews") return;

    const timeout = setTimeout(() => {
      fetchReviews();
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, reviewSearchTerm, reviewRatingFilter, reviewsPage]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "catalog" || catalogSubTab !== "products") return;

    const timeout = setTimeout(() => {
      fetchInventory();
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    adminUser,
    catalogSubTab,
    inventorySearch,
    inventoryStatusFilter,
    inventoryPage,
    inventoryRefreshKey,
  ]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "coupons") return;

    const timeout = setTimeout(() => {
      fetchCoupons();
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, couponSearchTerm, couponStatusFilter, couponsPage]);

  useEffect(() => {
    if (!adminUser) return;
    if (activeTab !== "pod") return;

    const timeout = setTimeout(() => {
      fetchPodSubmissions();
    }, 350);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminUser, podSearchTerm, podStatusFilter, podPage]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setCategories(data.data || []);
      } else {
        console.error("Error fetching categories:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/products?limit=100`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setProductsList(data.data.products || []);
        setProductsTotal(
          data.data.pagination?.total ?? (data.data.products || []).length,
        );
      } else {
        console.error("Error fetching products:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch products:", err);
    }
  };

  const fetchInventory = async () => {
    setLoadingInventory(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (inventorySearch.trim())
        params.append("search", inventorySearch.trim());
      if (inventoryStatusFilter) params.append("status", inventoryStatusFilter);
      params.append("page", inventoryPage);
      params.append("limit", 20);

      const res = await fetch(`${API}/products?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setInventoryProducts(data.data.products || []);
        setInventoryPagination(data.data.pagination || null);
      } else {
        console.error("Error fetching inventory:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch inventory:", err);
    } finally {
      setLoadingInventory(false);
    }
  };

  // After a create / edit / delete: refresh both the full list (coupon
  // picker, counts) and the currently shown inventory page
  const refreshProducts = () => {
    fetchProducts();
    setInventoryRefreshKey((k) => k + 1);
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (roleFilter !== "ALL") params.append("role", roleFilter);
      params.append("page", usersPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/users?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(data.data.users || []);
        setUsersPagination(data.data.pagination || null);
      } else {
        console.error("Error fetching users:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (orderStatusFilter !== "ALL")
        params.append("status", orderStatusFilter);
      params.append("page", ordersPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/orders?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setOrders(data.data.orders || []);
        setOrdersPagination(data.data.pagination || null);
        setTotalRevenue(data.data.totalRevenue || 0);
      } else {
        console.error("Error fetching orders:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleCreateShipment = async (orderId) => {
    if (
      !window.confirm(
        "Create a Shiprocket shipment for this order? This cannot be undone from here.",
      )
    ) {
      return;
    }

    setShippingOrderId(orderId);
    try {
      const token = localStorage.getItem("adminToken");
      const data = await createShipment(orderId, token);
      if (data.ok) {
        showFeedback("Shipment created in Shiprocket!", "success");
        fetchOrders();
      } else {
        showFeedback(data.message || "Failed to create shipment", "error");
      }
    } catch (err) {
      showFeedback("Network error creating shipment", "error");
    } finally {
      setShippingOrderId(null);
    }
  };

  const fetchContactSubmissions = async () => {
    setLoadingContacts(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      params.append("page", contactsPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/contact?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setContactSubmissions(data.data.submissions || []);
        setContactsPagination(data.data.pagination || null);
      } else {
        console.error("Error fetching contact submissions:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch contact submissions:", err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchPodSubmissions = async () => {
    setLoadingPod(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (podSearchTerm.trim()) params.append("search", podSearchTerm.trim());
      if (podStatusFilter !== "ALL") params.append("status", podStatusFilter);
      params.append("page", podPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/product-on-demand?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setPodSubmissions(data.data?.submissions || []);
        setPodPagination(data.data?.pagination || null);
      } else {
        console.error("Error fetching POD submissions:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch POD submissions:", err);
    } finally {
      setLoadingPod(false);
    }
  };

  const handleUpdatePodStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/product-on-demand/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("Status updated successfully!", "success");
        fetchPodSubmissions();
      } else {
        showFeedback(data.message || "Failed to update status", "error");
      }
    } catch (err) {
      showFeedback("Network error updating status", "error");
    }
  };

  const handleDeletePodSubmission = async (id) => {
    if (!window.confirm("Delete this Product On Demand submission?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/product-on-demand/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("Submission deleted!", "success");
        fetchPodSubmissions();
      } else {
        showFeedback(data.message || "Failed to delete submission", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting submission", "error");
    }
  };

  const fetchBlogs = async () => {
    setLoadingBlogs(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (blogSearchTerm) params.append("search", blogSearchTerm);
      if (blogStatusFilter !== "ALL") params.append("status", blogStatusFilter);
      params.append("page", blogsPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/blogs?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setBlogs(data.data.blogs || []);
        setBlogsPagination(data.data.pagination || null);
      } else {
        console.error("Error fetching blogs:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch blogs:", err);
    } finally {
      setLoadingBlogs(false);
    }
  };

  const resetBlogForm = () => {
    setBlogForm(emptyBlogForm);
    setEditingBlog(null);
  };

  const handleBlogFieldChange = (field, value) => {
    setBlogForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditBlogClick = (blog) => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title || "",
      slug: blog.slug || "",
      category: blog.category || "",
      image: blog.image || "",
      excerpt: blog.excerpt || "",
      content: blog.content || "",
      author: blog.author || "",
      status: blog.status || "draft",
    });
  };

  const handleSaveBlog = async (e) => {
    e.preventDefault();

    if (!blogForm.title.trim()) {
      showFeedback("Blog title is required", "error");
      return;
    }
    if (!blogForm.content.trim()) {
      showFeedback("Blog content is required", "error");
      return;
    }

    const isEditing = !!editingBlog;

    setSavingBlog(true);
    try {
      const token = localStorage.getItem("adminToken");
      // const payload = {
      //   title: blogForm.title.trim(),
      //   slug: blogForm.slug.trim() || undefined,
      //   category: blogForm.category.trim() || undefined,
      //   image: blogForm.image.trim() || undefined,
      //   excerpt: blogForm.excerpt.trim() || undefined,
      //   content: blogForm.content.trim(),
      //   author: blogForm.author.trim() || undefined,
      //   status: blogForm.status,
      // };
    const payload = {
  title: blogForm.title.trim(),
  slug: blogForm.slug.trim() || undefined,
  category: blogForm.category.trim() || undefined,
  image: blogForm.image.trim() || undefined,
  excerpt: blogForm.excerpt || "",
  content: blogForm.content || "",
  author: blogForm.author.trim() || undefined,
  status: blogForm.status,
};
      const url = isEditing ? `${API}/blogs/${editingBlog.id}` : `${API}/blogs`;
      const method = isEditing ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback(
          `Blog post ${isEditing ? "updated" : "created"} successfully!`,
          "success",
        );
        resetBlogForm();
        fetchBlogs();
      } else {
        showFeedback(
          data.message ||
            `Failed to ${isEditing ? "update" : "create"} blog post`,
          "error",
        );
      }
    } catch (err) {
      showFeedback(
        `Network error ${isEditing ? "updating" : "creating"} blog post`,
        "error",
      );
    } finally {
      setSavingBlog(false);
    }
  };
  const handleDeleteBlog = async (blogId) => {
    if (
      !window.confirm("Delete this blog post? This action cannot be undone.")
    ) {
      return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/blogs/${blogId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("Blog post deleted successfully!", "success");
        if (editingBlog?.id === blogId) resetBlogForm();
        fetchBlogs();
      } else {
        showFeedback(data.message || "Failed to delete blog post", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting blog post", "error");
    }
  };

  const fetchCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (couponSearchTerm) params.append("search", couponSearchTerm);
      if (couponStatusFilter !== "ALL") {
        params.append(
          "isActive",
          couponStatusFilter === "active" ? "true" : "false",
        );
      }
      params.append("page", couponsPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/coupons?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setCoupons(data.data.coupons || []);
        setCouponsPagination(data.data.pagination || null);
      } else {
        console.error("Error fetching coupons:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch coupons:", err);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const resetCouponForm = () => {
    setCouponForm(emptyCouponForm);
    setEditingCoupon(null);
  };

  const handleCouponFieldChange = (field, value) => {
    setCouponForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCouponScopeToggle = (field, id) => {
    setCouponForm((prev) => {
      const current = prev[field];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...prev, [field]: next };
    });
  };

  const handleEditCouponClick = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code || "",
      description: coupon.description || "",
      discountType: coupon.discountType || "percentage",
      discountValue:
        coupon.discountValue != null ? String(coupon.discountValue) : "",
      maxDiscountAmount:
        coupon.maxDiscountAmount != null
          ? String(coupon.maxDiscountAmount)
          : "",
      minOrderAmount:
        coupon.minOrderAmount != null ? String(coupon.minOrderAmount) : "",
      applicability: coupon.applicability || "all",
      categoryIds: (coupon.categories || []).map((c) => c.id),
      productIds: (coupon.products || []).map((p) => p.id),
      isSingleUse: !!coupon.isSingleUse,
      requiresLogin: !!coupon.requiresLogin,
      isActive: coupon.isActive !== false,
      expiresAt: coupon.expiresAt ? coupon.expiresAt.slice(0, 10) : "",
    });
  };

  const handleSaveCoupon = async (e) => {
    e.preventDefault();

    if (!couponForm.code.trim()) {
      showFeedback("Coupon code is required", "error");
      return;
    }
    if (!couponForm.discountValue || Number(couponForm.discountValue) <= 0) {
      showFeedback("Discount value must be a positive number", "error");
      return;
    }
    if (
      couponForm.applicability === "category" &&
      couponForm.categoryIds.length === 0
    ) {
      showFeedback(
        "Select at least one category for a category-scoped coupon",
        "error",
      );
      return;
    }
    if (
      couponForm.applicability === "product" &&
      couponForm.productIds.length === 0
    ) {
      showFeedback(
        "Select at least one product for a product-scoped coupon",
        "error",
      );
      return;
    }

    const isEditing = !!editingCoupon;
    setSavingCoupon(true);

    try {
      const token = localStorage.getItem("adminToken");
      const payload = {
        code: couponForm.code.trim(),
        description: couponForm.description.trim() || undefined,
        discountType: couponForm.discountType,
        discountValue: Number(couponForm.discountValue),
        maxDiscountAmount:
          couponForm.maxDiscountAmount !== ""
            ? Number(couponForm.maxDiscountAmount)
            : null,
        minOrderAmount:
          couponForm.minOrderAmount !== ""
            ? Number(couponForm.minOrderAmount)
            : 0,
        applicability: couponForm.applicability,
        categoryIds:
          couponForm.applicability === "category"
            ? couponForm.categoryIds
            : undefined,
        productIds:
          couponForm.applicability === "product"
            ? couponForm.productIds
            : undefined,
        isSingleUse: couponForm.isSingleUse,
        requiresLogin: couponForm.requiresLogin,
        isActive: couponForm.isActive,
        expiresAt: couponForm.expiresAt || null,
      };

      const url = isEditing
        ? `${API}/coupons/${editingCoupon.id}`
        : `${API}/coupons`;
      const res = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (res.ok) {
        showFeedback(
          `Coupon ${isEditing ? "updated" : "created"} successfully!`,
          "success",
        );
        resetCouponForm();
        fetchCoupons();
      } else {
        showFeedback(
          data.message || `Failed to ${isEditing ? "update" : "create"} coupon`,
          "error",
        );
      }
    } catch (err) {
      showFeedback(
        `Network error ${isEditing ? "updating" : "creating"} coupon`,
        "error",
      );
    } finally {
      setSavingCoupon(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm("Delete this coupon? This action cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/coupons/${couponId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("Coupon deleted successfully!", "success");
        if (editingCoupon?.id === couponId) resetCouponForm();
        fetchCoupons();
      } else {
        showFeedback(data.message || "Failed to delete coupon", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting coupon", "error");
    }
  };

  const fetchReviews = async () => {
    setLoadingReviews(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      if (reviewSearchTerm) params.append("search", reviewSearchTerm);
      if (reviewRatingFilter !== "ALL")
        params.append("rating", reviewRatingFilter);
      params.append("page", reviewsPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/reviews?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setReviews(data.data.reviews || []);
        setReviewsPagination(data.data.pagination || null);
      } else {
        console.error("Error fetching reviews:", data.message);
      }
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review? This action cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/reviews/${reviewId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("Review deleted successfully!", "success");
        fetchReviews();
      } else {
        showFeedback(data.message || "Failed to delete review", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting review", "error");
    }
  };

  const fetchVaidikSubmissions = async () => {
    setLoadingVaidik(true);
    try {
      const token = localStorage.getItem("adminToken");
      const params = new URLSearchParams();
      params.append("page", vaidikPage);
      params.append("limit", 10);

      const res = await fetch(`${API}/vaidik-vidhya?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setVaidikSubmissions(data.data.submissions || []);
        setVaidikPagination(data.data.pagination || null);
      } else {
        console.error(
          "Error fetching Vaidik Vidhya submissions:",
          data.message,
        );
      }
    } catch (err) {
      console.error("Failed to fetch Vaidik Vidhya submissions:", err);
    } finally {
      setLoadingVaidik(false);
    }
  };

  // Uploaded images come back as "/uploads/..." unless the backend serves them from a CDN.
  const getUploadedImageSrc = (url) =>
    /^https?:\/\//.test(url)
      ? url
      : `${API.replace(/\/api\/v1\/?$/, "")}${url}`;

  const clearCategoryImage = () => {
    if (catImagePreview) URL.revokeObjectURL(catImagePreview);
    setCatImageFile(null);
    setCatImagePreview("");
    setCatExistingImage("");
    setCatRemoveImage(false);
  };

  const handleCategoryImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      showFeedback("Only JPEG, PNG or WebP images are allowed", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showFeedback("Image must be 5 MB or smaller", "error");
      e.target.value = "";
      return;
    }

    if (catImagePreview) URL.revokeObjectURL(catImagePreview);
    setCatImageFile(file);
    setCatImagePreview(URL.createObjectURL(file));
    setCatRemoveImage(false);
  };

  const resetCategoryForm = () => {
    setCatName("");
    setCatSlug("");
    setCatDescription("");
    setCatParentId("");
    setEditingCategory(null);
    setCatFormMode(null);
    clearCategoryImage();
  };

  const scrollToCategoryForm = () => {
    setTimeout(() => {
      document
        .getElementById("cat-form-card")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  // Opens a blank, dedicated form for a new category or a new subcategory.
  // `parentId` pre-selects the parent when adding from a category's row.
  const openCreateCategoryForm = (mode, parentId = "") => {
    resetCategoryForm();
    setCatFormMode(mode);
    if (mode === "subcategory") setCatParentId(parentId);
    scrollToCategoryForm();
  };

  const handleEditCategoryClick = (cat) => {
    clearCategoryImage();
    setCatFormMode("category");
    scrollToCategoryForm();
    setEditingCategory({ type: "category", id: cat.id });
    setCatName(cat.name);
    setCatSlug(cat.slug);
    setCatDescription(cat.description || "");
    setCatParentId("");
  };

  const handleEditSubcategoryClick = (sub, parentId) => {
    setCatFormMode("subcategory");
    scrollToCategoryForm();
    setEditingCategory({ type: "subcategory", id: sub.id });
    setCatName(sub.name);
    setCatSlug(sub.slug);
    setCatDescription(sub.description || "");
    setCatParentId(sub.categoryId || parentId);
    clearCategoryImage();
    setCatExistingImage(sub.imageUrl || "");
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!catName.trim()) {
      showFeedback("Category name is required", "error");
      return;
    }
    const isEditing = !!editingCategory;
    const isSubcategory = isEditing
      ? editingCategory.type === "subcategory"
      : catFormMode === "subcategory";

    if (isSubcategory && !catParentId) {
      showFeedback("Please select a parent category", "error");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");

      let url;
      let method;
      if (editingCategory?.type === "category") {
        url = `${API}/categories/${editingCategory.id}`;
        method = "PUT";
      } else if (editingCategory?.type === "subcategory") {
        url = `${API}/categories/subcategories/${editingCategory.id}`;
        method = "PUT";
      } else {
        url = isSubcategory
          ? `${API}/categories/${catParentId}/subcategories`
          : `${API}/categories`;
        method = "POST";
      }

      const payload = {
        name: catName,
        description: catDescription.trim() ? catDescription.trim() : undefined,
      };
      if (isEditing) {
        payload.slug = catSlug.trim();
      } else if (catSlug.trim()) {
        payload.slug = catSlug.trim();
      }

      if (editingCategory?.type === "subcategory") {
        payload.categoryId = catParentId;
      }

      // Subcategories can carry an image, which needs a multipart request.
      // Without an image change the original JSON request is kept.
      const hasImageChange =
        isSubcategory && (Boolean(catImageFile) || catRemoveImage);
      const headers = { Authorization: `Bearer ${token}` };
      let body;
      if (hasImageChange) {
        body = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined) body.append(key, value);
        });
        if (catImageFile) body.append("image", catImageFile);
        else body.append("removeImage", "true");
        // The browser sets the multipart boundary itself
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(payload);
      }

      const res = await fetch(url, { method, headers, body });

      const data = await res.json();
      if (res.ok) {
        const label = isSubcategory ? "Subcategory" : "Category";
        showFeedback(
          `${label} ${isEditing ? "updated" : "created"} successfully!`,
          "success",
        );
        resetCategoryForm();
        fetchCategories();
      } else {
        showFeedback(
          data.message ||
            `Failed to ${isEditing ? "update" : "create"} category`,
          "error",
        );
      }
    } catch (err) {
      showFeedback(
        `Network error ${isEditing ? "updating" : "creating"} category`,
        "error",
      );
    }
  };

  // Hide / show a category or subcategory on the public site and shop.
  const handleToggleVisibility = async (item, isSubcategory) => {
    const nextActive = item.isActive === false; // currently hidden -> show
    const label = isSubcategory ? "subcategory" : "category";
    const message = nextActive
      ? `Show this ${label} on the website again?`
      : `Hide this ${label}? It${isSubcategory ? "" : ", its subcategories"} and all its products will disappear from the website and the shop. Nothing is deleted.`;
    if (!window.confirm(message)) return;

    try {
      const token = localStorage.getItem("adminToken");
      const url = isSubcategory
        ? `${API}/categories/subcategories/${item.id}`
        : `${API}/categories/${item.id}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isActive: nextActive }),
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback(
          `${isSubcategory ? "Subcategory" : "Category"} ${nextActive ? "is now visible" : "hidden"} on the website`,
          "success",
        );
        fetchCategories();
      } else {
        showFeedback(data.message || `Failed to update ${label}`, "error");
      }
    } catch (err) {
      showFeedback(`Network error updating ${label}`, "error");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (
      !window.confirm(
        "Delete this category? This cascades to all its subcategories and products, and is blocked if any of those products already have orders placed against them.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/categories/${categoryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("Category deleted successfully!", "success");
        if (editingCategory?.id === categoryId) resetCategoryForm();
        fetchCategories();
      } else {
        showFeedback(data.message || "Failed to delete category", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting category", "error");
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    if (
      !window.confirm(
        "Delete this subcategory? This cascades to all its products, and is blocked if any of them already have orders placed against them.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(
        `${API}/categories/subcategories/${subcategoryId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await res.json();
      if (res.ok) {
        showFeedback("Subcategory deleted successfully!", "success");
        if (editingCategory?.id === subcategoryId) resetCategoryForm();
        fetchCategories();
      } else {
        showFeedback(data.message || "Failed to delete subcategory", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting subcategory", "error");
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!prodCategoryId) {
      showFeedback("Please select a category", "error");
      return;
    }
    if (!prodName.trim()) {
      showFeedback("Product name is required", "error");
      return;
    }
    if (!prodPrice || parseFloat(prodPrice) < 0) {
      showFeedback("Please enter a valid price", "error");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const payload = {
        categoryId: prodCategoryId,
        subcategoryId: prodSubcategoryId || undefined,
        name: prodName,
        slug: prodSlug.trim() ? prodSlug.trim() : undefined,
        description: prodDescription.trim()
          ? prodDescription.trim()
          : undefined,
        price: parseFloat(prodPrice),
        mukhiCount: prodMukhiCount ? parseInt(prodMukhiCount, 10) : undefined,
        origin: prodOrigin.trim() ? prodOrigin.trim() : undefined,
        isCertified: prodIsCertified,
        certificationUrl: prodCertificationUrl.trim()
          ? prodCertificationUrl.trim()
          : undefined,
        beadSizeMm: prodBeadSizeMm.trim() ? prodBeadSizeMm.trim() : undefined,
        stockQuantity: prodStockQuantity ? parseInt(prodStockQuantity, 10) : 0,
        status: prodStatus,
      };

      const res = await fetch(`${API}/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        showFeedback(data.message || "Failed to create product", "error");
        return;
      }

      const createdProduct = data.data;

      // Now upload images if selected
      if (prodImages && prodImages.length > 0) {
        const formData = new FormData();
        for (let i = 0; i < prodImages.length; i++) {
          formData.append("images", prodImages[i]);
        }

        const imgRes = await fetch(
          `${API}/products/${createdProduct.id}/images`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        const imgData = await imgRes.json();
        if (!imgRes.ok) {
          showFeedback(
            `Product created, but image upload failed: ${imgData.message}`,
            "warning",
          );
        } else {
          showFeedback("Product and images uploaded successfully!", "success");
        }
      } else {
        showFeedback("Product created successfully!", "success");
      }

      // Reset form
      setProdCategoryId("");
      setProdSubcategoryId("");
      setProdName("");
      setProdSlug("");
      setProdDescription("");
      setProdPrice("");
      setProdMukhiCount("");
      setProdOrigin("");
      setProdIsCertified(false);
      setProdCertificationUrl("");
      setProdBeadSizeMm("");
      setProdStockQuantity("");
      setProdStatus("draft");
      setProdImages([]);

      const fileInput = document.getElementById("product-images-input");
      if (fileInput) fileInput.value = "";

      refreshProducts();
    } catch (err) {
      showFeedback("Network error creating product", "error");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this product? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/products/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("Product deleted successfully!", "success");
        refreshProducts();
      } else {
        showFeedback(data.message || "Failed to delete product", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting product", "error");
    }
  };

  const openEditProduct = (prod) => {
    setEditingProduct(prod);
    setEditProductForm({
      categoryId: prod.categoryId || "",
      subcategoryId: prod.subcategoryId || "",
      name: prod.name || "",
      slug: prod.slug || "",
      description: prod.description || "",
      price: prod.price != null ? String(prod.price) : "",
      mukhiCount: prod.mukhiCount != null ? String(prod.mukhiCount) : "",
      origin: prod.origin || "",
      isCertified: !!prod.isCertified,
      certificationUrl: prod.certificationUrl || "",
      beadSizeMm: prod.beadSizeMm || "",
      stockQuantity:
        prod.stockQuantity != null ? String(prod.stockQuantity) : "",
      status: prod.status || "draft",
    });
  };

  const closeEditProduct = () => {
    setEditingProduct(null);
    setEditProductForm(null);
  };

  const handleEditProductFieldChange = (field, value) => {
    setEditProductForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct || !editProductForm) return;

    if (!editProductForm.categoryId) {
      showFeedback("Please select a category", "error");
      return;
    }
    if (!editProductForm.name.trim()) {
      showFeedback("Product name is required", "error");
      return;
    }
    if (!editProductForm.slug.trim()) {
      showFeedback("Slug cannot be empty", "error");
      return;
    }
    if (editProductForm.price === "" || parseFloat(editProductForm.price) < 0) {
      showFeedback("Please enter a valid price", "error");
      return;
    }

    setSavingProduct(true);
    try {
      const token = localStorage.getItem("adminToken");
      const payload = {
        categoryId: editProductForm.categoryId,
        subcategoryId: editProductForm.subcategoryId || null,
        name: editProductForm.name.trim(),
        // Sent explicitly (not omitted-when-empty like the create form) so a
        // cleared field actually clears the value instead of being ignored.
        slug: editProductForm.slug.trim(),
        description: editProductForm.description.trim(),
        price: parseFloat(editProductForm.price),
        mukhiCount:
          editProductForm.mukhiCount !== ""
            ? parseInt(editProductForm.mukhiCount, 10)
            : null,
        origin: editProductForm.origin.trim(),
        isCertified: editProductForm.isCertified,
        certificationUrl: editProductForm.certificationUrl.trim(),
        beadSizeMm: editProductForm.beadSizeMm.trim(),
        stockQuantity:
          editProductForm.stockQuantity !== ""
            ? parseInt(editProductForm.stockQuantity, 10)
            : 0,
        status: editProductForm.status,
      };

      const res = await fetch(`${API}/products/${editingProduct.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        showFeedback("Product updated successfully!", "success");
        closeEditProduct();
        refreshProducts();
      } else {
        showFeedback(data.message || "Failed to update product", "error");
      }
    } catch (err) {
      showFeedback("Network error updating product", "error");
    } finally {
      setSavingProduct(false);
    }
  };

  const fetchOptionGroups = async (productId) => {
    setLoadingProductOptions(true);
    try {
      const data = await getProductOptions(productId);
      if (data.success) {
        setProductOptionGroups(data.data || []);
      } else {
        showFeedback(data.message || "Failed to load option groups", "error");
      }
    } catch (err) {
      showFeedback("Network error loading option groups", "error");
    } finally {
      setLoadingProductOptions(false);
    }
  };

  const openOptionsPanel = (prod) => {
    setOptionsPanelProduct(prod);
    setGroupForm(emptyGroupForm);
    fetchOptionGroups(prod.id);
  };

  const closeOptionsPanel = () => {
    setOptionsPanelProduct(null);
    setProductOptionGroups([]);
    setGroupForm(emptyGroupForm);
  };

  const loadGroupIntoForm = (group) => {
    setGroupForm({
      id: group.id,
      name: group.name,
      selectionType: group.selectionType,
      isRequired: group.isRequired,
      showWhenGroup: group.showWhenGroup || "",
      showWhenPath: group.showWhenPath || "",
      choices: group.choices.map((choice) => ({
        label: choice.label,
        priceDelta: String(choice.priceDelta),
        imageUrl: choice.imageUrl || "",
      })),
    });
  };

  const handleChoiceFieldChange = (index, field, value) => {
    setGroupForm((prev) => {
      const choices = [...prev.choices];
      choices[index] = { ...choices[index], [field]: value };
      return { ...prev, choices };
    });
  };

  const handleAddChoiceRow = () => {
    setGroupForm((prev) => ({
      ...prev,
      choices: [...prev.choices, { label: "", priceDelta: "", imageUrl: "" }],
    }));
  };

  const handleRemoveChoiceRow = (index) => {
    setGroupForm((prev) => ({
      ...prev,
      choices: prev.choices.filter((_, i) => i !== index),
    }));
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();

    if (!groupForm.name.trim()) {
      showFeedback("Please enter an option group name", "error");
      return;
    }

    const choices = groupForm.choices
      .filter((choice) => choice.label.trim())
      .map((choice, index) => ({
        label: choice.label.trim(),
        priceDelta: choice.priceDelta ? Number(choice.priceDelta) : 0,
        imageUrl: choice.imageUrl.trim() || undefined,
        displayOrder: index,
      }));

    if (choices.length === 0) {
      showFeedback("Please add at least one choice with a label", "error");
      return;
    }

    const payload = {
      name: groupForm.name.trim(),
      selectionType: groupForm.selectionType,
      isRequired: groupForm.isRequired,
      // Both empty clears the condition; one without the other is rejected by the server.
      showWhenGroup: groupForm.showWhenGroup.trim(),
      showWhenPath: groupForm.showWhenPath.trim(),
      choices,
    };

    setSavingGroup(true);
    try {
      const data = groupForm.id
        ? await updateOptionGroup(groupForm.id, payload)
        : await createOptionGroup(optionsPanelProduct.id, payload);

      if (data.success) {
        showFeedback(
          groupForm.id ? "Option group updated!" : "Option group created!",
          "success",
        );
        setGroupForm(emptyGroupForm);
        fetchOptionGroups(optionsPanelProduct.id);
      } else {
        showFeedback(data.message || "Failed to save option group", "error");
      }
    } catch (err) {
      showFeedback("Network error saving option group", "error");
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("Delete this option group and all its choices?")) {
      return;
    }

    try {
      const data = await deleteOptionGroup(groupId);
      if (data.success) {
        showFeedback("Option group deleted!", "success");
        fetchOptionGroups(optionsPanelProduct.id);
      } else {
        showFeedback(data.message || "Failed to delete option group", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting option group", "error");
    }
  };

  const showFeedback = (text, type) => {
    setFeedbackMessage({ text, type });
    setTimeout(() => {
      setFeedbackMessage(null);
    }, 5000);
  };

  const getProductImageUrl = (product) => {
    if (product.images && product.images.length > 0) {
      const path = product.images[0].imageUrl;
      if (path.startsWith("http")) return path;
      const cleanApi = API.endsWith("/api/v1") ? API.slice(0, -7) : API;
      return `${cleanApi}${path}`;
    }
    return null;
  };

  // Helper calculation
  const pendingAstrologersCount = astrologers.filter(
    (a) => !a.isApproved,
  ).length;

  // Actions
  const toggleAstrologerApproval = (id) => {
    setAstrologers((prev) =>
      prev.map((astro) =>
        astro.id === id ? { ...astro, isApproved: !astro.isApproved } : astro,
      ),
    );
  };

  const deleteUser = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API}/users/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        showFeedback("User deleted successfully!", "success");
        fetchUsers();
      } else {
        showFeedback(data.message || "Failed to delete user", "error");
      }
    } catch (err) {
      showFeedback("Network error deleting user", "error");
    }
  };

  // Render components based on activeTab
  const renderDashboardOverview = () => {
    const recentOrders = orders.slice(0, 4);
    const pendingAstrologers = astrologers.filter((a) => !a.isApproved);

    return (
      <div className="overview-tab">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-info">
              <span className="stat-label">Total Revenue</span>
              <h3 className="stat-value">₹{totalRevenue.toLocaleString()}</h3>
            </div>
            <div className="stat-icon revenue-icon">
              <FaDollarSign />
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-info">
              <span className="stat-label">Total Users</span>
              <h3 className="stat-value">
                {usersPagination?.total ?? users.length}
              </h3>
            </div>
            <div className="stat-icon users-icon">
              <FaUsers />
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-info">
              <span className="stat-label">Astrologers</span>
              <h3 className="stat-value">{astrologers.length}</h3>
            </div>
            <div className="stat-icon astro-icon">
              <FaUserAstronaut />
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-info">
              <span className="stat-label">Pending Approvals</span>
              <h3 className="stat-value highlight-alert">
                {pendingAstrologersCount}
              </h3>
            </div>
            <div className="stat-icon alert-icon">
              <FaUserCheck />
            </div>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Recent Orders */}
          <div className="dashboard-box">
            <div className="box-header">
              <h4>Recent Orders</h4>
              <button
                className="view-all-btn"
                onClick={() => setActiveTab("orders")}
              >
                View All
              </button>
            </div>
            <div className="box-content">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center py-8 text-gray-500"
                      >
                        No orders yet.
                      </td>
                    </tr>
                  ) : (
                    recentOrders.map((ord) => (
                      <tr key={ord.id}>
                        <td className="font-semibold">{ord.orderNumber}</td>
                        <td>{ord.user?.name || ord.guestName || "Guest"}</td>
                        <td>₹{Number(ord.total).toLocaleString()}</td>
                        <td>
                          <span className={`status-badge ${ord.status}`}>
                            {ord.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pending Approvals */}
          <div className="dashboard-box">
            <div className="box-header">
              <h4>Pending Astrologers</h4>
              <button
                className="view-all-btn"
                onClick={() => setActiveTab("astrologers")}
              >
                View All
              </button>
            </div>
            <div className="box-content">
              {pendingAstrologers.length === 0 ? (
                <div className="empty-state">
                  <FaCheckCircle className="success-check-icon" />
                  <p>All astrologer profiles approved!</p>
                </div>
              ) : (
                <ul className="pending-list">
                  {pendingAstrologers.map((astro) => (
                    <li key={astro.id} className="pending-item">
                      <div className="pending-info">
                        <span className="pending-name">{astro.name}</span>
                        <span className="pending-meta">
                          {astro.specialization.join(", ")} |{" "}
                          {astro.experienceYrs} yrs exp
                        </span>
                      </div>
                      <button
                        className="approve-btn-mini"
                        onClick={() => toggleAstrologerApproval(astro.id)}
                      >
                        <FaCheck /> Approve
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUsersPanel = () => {
    return (
      <div className="panel-tab">
        <div className="panel-filters">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setUsersPage(1);
              }}
            />
          </div>
          <div className="select-wrapper">
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setUsersPage(1);
              }}
            >
              <option value="ALL">All Roles</option>
              <option value="USER">User</option>
              <option value="ASTROLOGER">Astrologer</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="dashboard-table main-table">
            <thead>
              <tr>
                <th>User Details</th>
                <th>Phone Number</th>
                <th>Role</th>
                <th>Registration Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingUsers ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    No users found matching query.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="user-details-cell">
                        <span className="user-name">{u.name}</span>
                        <span className="user-email">{u.email}</span>
                      </div>
                    </td>
                    <td>{u.phone || "N/A"}</td>
                    <td>
                      <span className={`role-badge ${u.role.toLowerCase()}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>
                      {u.role !== "ADMIN" ? (
                        <button
                          className="delete-btn"
                          onClick={() => deleteUser(u.id)}
                        >
                          Delete
                        </button>
                      ) : (
                        <span className="action-disabled">Protected</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {usersPagination && usersPagination.pages > 1 && (
          <div className="pagination-controls">
            <button
              disabled={usersPage <= 1}
              onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>
              Page {usersPagination.page} of {usersPagination.pages}
            </span>
            <button
              disabled={usersPage >= usersPagination.pages}
              onClick={() =>
                setUsersPage((p) => Math.min(usersPagination.pages, p + 1))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderAstrologersPanel = () => {
    const filteredAstros = astrologers.filter((a) => {
      const matchSearch = a.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchStatus =
        statusFilter === "ALL" ||
        (statusFilter === "APPROVED" && a.isApproved) ||
        (statusFilter === "PENDING" && !a.isApproved);
      return matchSearch && matchStatus;
    });

    return (
      <div className="panel-tab">
        <div className="panel-filters">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search astrologer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="select-wrapper">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="dashboard-table main-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Specialization</th>
                <th>Experience</th>
                <th>Hourly Rate</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAstros.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    No astrologers found.
                  </td>
                </tr>
              ) : (
                filteredAstros.map((a) => (
                  <tr key={a.id}>
                    <td className="font-semibold">{a.name}</td>
                    <td>
                      <div className="tags-container">
                        {a.specialization.map((spec, idx) => (
                          <span key={idx} className="spec-tag">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{a.experienceYrs} Years</td>
                    <td>₹{a.hourlyRate}/hr</td>
                    <td>
                      <div className="rating-cell">
                        <FaStar className="star-icon" /> {a.rating}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${a.isApproved ? "approved" : "pending"}`}
                      >
                        {a.isApproved ? "Approved" : "Pending"}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`action-toggle-btn ${a.isApproved ? "suspend" : "approve"}`}
                        onClick={() => toggleAstrologerApproval(a.id)}
                      >
                        {a.isApproved ? "Suspend" : "Approve"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderOrdersPanel = () => {
    return (
      <div className="panel-tab">
        <div className="panel-filters">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by order number, customer name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setOrdersPage(1);
              }}
            />
          </div>
          <div className="select-wrapper">
            <select
              value={orderStatusFilter}
              onChange={(e) => {
                setOrderStatusFilter(e.target.value);
                setOrdersPage(1);
              }}
            >
              <option value="ALL">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="packed">Packed</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="dashboard-table main-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Order Date</th>
                <th>Status</th>
                <th>Shipping</th>
              </tr>
            </thead>
            <tbody>
              {loadingOrders ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    Loading orders...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    No orders found.
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id}>
                    <td className="font-mono font-semibold">
                      {o.orderNumber}
                      {o.deliveryPreferredDate && (
                        <div className="user-email">
                          Deliver on{" "}
                          {new Date(o.deliveryPreferredDate).toLocaleDateString(
                            "en-IN",
                            { timeZone: "UTC", day: "numeric", month: "short" },
                          )}
                          {o.deliveryNote ? ` - ${o.deliveryNote}` : ""}
                        </div>
                      )}
                    </td>
                    <td>{o.user?.name || o.guestName || "Guest"}</td>
                    <td>₹{Number(o.total).toLocaleString()}</td>
                    <td>
                      {o.paymentMethod === "cod" ? "Cash on Delivery" : "PayU"}
                      {Number(o.advanceAmount) > 0 && (
                        <div className="user-email">
                          Advance ₹
                          {Number(o.advanceAmount).toLocaleString("en-IN")}{" "}
                          {o.status === "paid" ? "paid" : "pending"}
                          {" · "}collect ₹
                          {(
                            Number(o.total) - Number(o.advanceAmount)
                          ).toLocaleString("en-IN")}{" "}
                          on delivery
                        </div>
                      )}
                      {Number(o.prepaidDiscount) > 0 && (
                        <div className="user-email">
                          Prepaid discount ₹
                          {Number(o.prepaidDiscount).toLocaleString("en-IN")}
                        </div>
                      )}
                      {o.paymentMethod === "cod" && o.codConfirmedAt && (
                        <div className="user-email">
                          ✓ Confirmed by the customer
                        </div>
                      )}
                      {o.paymentMethod === "cod" &&
                        !o.codConfirmedAt &&
                        !["cancelled", "delivered"].includes(o.status) && (
                          <div className="ac-warn">
                            COD not confirmed by the customer yet
                          </div>
                        )}
                    </td>
                    <td>{new Date(o.createdAt).toLocaleString()}</td>
                    <td>
                      <span className={`status-badge ${o.status}`}>
                        {o.status}
                      </span>
                      {o.status === "cancelled" && o.cancelReason && (
                        <div className="user-email">{o.cancelReason}</div>
                      )}
                      {o.refundRequired && (
                        <div className="ac-warn">Refund due to customer</div>
                      )}
                    </td>
                    <td>
                      {o.shiprocketShipmentId ? (
                        <span
                          className="status-badge success"
                          title={`Shiprocket order ${o.shiprocketOrderId}`}
                        >
                          {o.awbCode ? `AWB ${o.awbCode}` : "Shipment Created"}
                        </span>
                      ) : o.status === "paid" ? (
                        <button
                          className="edit-btn"
                          disabled={shippingOrderId === o.id}
                          onClick={() => handleCreateShipment(o.id)}
                        >
                          {shippingOrderId === o.id
                            ? "Creating..."
                            : "Create Shipment"}
                        </button>
                      ) : (
                        <span className="action-disabled">
                          {o.status === "pending" ? "Awaiting payment" : "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {ordersPagination && ordersPagination.pages > 1 && (
          <div className="pagination-controls">
            <button
              disabled={ordersPage <= 1}
              onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>
              Page {ordersPagination.page} of {ordersPagination.pages}
            </span>
            <button
              disabled={ordersPage >= ordersPagination.pages}
              onClick={() =>
                setOrdersPage((p) => Math.min(ordersPagination.pages, p + 1))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderContactPanel = () => {
    return (
      <div className="panel-tab">
        <div className="table-container">
          <table className="dashboard-table main-table">
            <thead>
              <tr>
                <th>Contact Details</th>
                <th>Age</th>
                <th>Category</th>
                <th>Message</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {loadingContacts ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    Loading contact submissions...
                  </td>
                </tr>
              ) : contactSubmissions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    No contact form submissions yet.
                  </td>
                </tr>
              ) : (
                contactSubmissions.map((c) => {
                  const isExpanded = expandedContactId === c.id;
                  const isLong = c.message && c.message.length > 120;
                  const displayMessage =
                    isExpanded || !isLong
                      ? c.message
                      : `${c.message.slice(0, 120)}...`;

                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="user-details-cell">
                          <span className="user-name">{c.name}</span>
                          <span className="user-email">{c.email}</span>
                          <span className="user-email">{c.phone}</span>
                        </div>
                      </td>
                      <td>{c.age ?? "N/A"}</td>
                      <td>{c.category || "N/A"}</td>
                      <td style={{ maxWidth: "320px", whiteSpace: "pre-wrap" }}>
                        {displayMessage}
                        {isLong && (
                          <button
                            type="button"
                            className="view-all-btn"
                            style={{ marginLeft: "8px" }}
                            onClick={() =>
                              setExpandedContactId(isExpanded ? null : c.id)
                            }
                          >
                            {isExpanded ? "Show less" : "Read more"}
                          </button>
                        )}
                      </td>
                      <td>{new Date(c.createdAt).toLocaleString()}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {contactsPagination && contactsPagination.pages > 1 && (
          <div className="pagination-controls">
            <button
              disabled={contactsPage <= 1}
              onClick={() => setContactsPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>
              Page {contactsPagination.page} of {contactsPagination.pages}
            </span>
            <button
              disabled={contactsPage >= contactsPagination.pages}
              onClick={() =>
                setContactsPage((p) =>
                  Math.min(contactsPagination.pages, p + 1),
                )
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderVaidikVidhyaPanel = () => {
    return (
      <div className="panel-tab">
        <div className="table-container">
          <table className="dashboard-table main-table">
            <thead>
              <tr>
                <th>Full Name</th>
                <th>Date of Birth</th>
                <th>Time & Place of Birth</th>
                <th>Delivery Address</th>
                <th>Mobile Number</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {loadingVaidik ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    Loading submissions...
                  </td>
                </tr>
              ) : vaidikSubmissions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No Vaidik Vidhya submissions yet.
                  </td>
                </tr>
              ) : (
                vaidikSubmissions.map((s) => (
                  <tr key={s.id}>
                    <td className="font-semibold">{s.fullName}</td>
                    <td>{new Date(s.dateOfBirth).toLocaleDateString()}</td>
                    <td>
                      <div className="user-details-cell">
                        <span className="user-name">{s.timeOfBirth}</span>
                        <span className="user-email">{s.placeOfBirth}</span>
                      </div>
                    </td>
                    <td style={{ maxWidth: "260px", whiteSpace: "pre-wrap" }}>
                      {s.deliveryAddress}
                      <br />
                      <strong>PIN:</strong> {s.pincode}
                    </td>
                    <td>{s.mobileNumber}</td>
                    <td>{new Date(s.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {vaidikPagination && vaidikPagination.pages > 1 && (
          <div className="pagination-controls">
            <button
              disabled={vaidikPage <= 1}
              onClick={() => setVaidikPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>
              Page {vaidikPagination.page} of {vaidikPagination.pages}
            </span>
            <button
              disabled={vaidikPage >= vaidikPagination.pages}
              onClick={() =>
                setVaidikPage((p) => Math.min(vaidikPagination.pages, p + 1))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderProductOnDemandPanel = () => {
    const resolveImg = (path) => {
      if (!path) return null;
      if (path.startsWith("http")) return path;
      const cleanApi = API.endsWith("/api/v1") ? API.slice(0, -7) : API;
      return `${cleanApi}${path}`;
    };

    return (
      <div className="panel-tab">
        <div className="panel-filters">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, phone, email, ref, category..."
              value={podSearchTerm}
              onChange={(e) => {
                setPodSearchTerm(e.target.value);
                setPodPage(1);
              }}
            />
          </div>
          <div className="select-wrapper">
            <select
              value={podStatusFilter}
              onChange={(e) => {
                setPodStatusFilter(e.target.value);
                setPodPage(1);
              }}
            >
              <option value="ALL">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="contacted">Contacted</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="dashboard-table main-table">
            <thead>
              <tr>
                <th>Ref / Date</th>
                <th>Customer Info</th>
                <th>Category & Metal</th>
                <th>Budget & DOB</th>
                <th>Image Preview</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingPod ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    Loading Product On Demand submissions...
                  </td>
                </tr>
              ) : podSubmissions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    No Product On Demand submissions found.
                  </td>
                </tr>
              ) : (
                podSubmissions.map((sub) => {
                  const imgUrl = resolveImg(sub.imageUrl);
                  return (
                    <tr key={sub.id}>
                      <td>
                        <div className="product-details-cell">
                          <span
                            className="user-name"
                            style={{ color: "#C9A227", fontWeight: "bold" }}
                          >
                            {sub.ref}
                          </span>
                          <span className="user-email">
                            {new Date(sub.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="user-details-cell">
                          <span className="user-name">{sub.fullName}</span>
                          <span className="user-email">📞 {sub.phone}</span>
                          {sub.email && (
                            <span className="user-email">✉️ {sub.email}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="product-details-cell">
                          <span className="user-name">{sub.category}</span>
                          <span className="user-email">
                            Metal: {sub.metalPreference || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="product-details-cell">
                          <span className="user-name">
                            ₹{sub.budget || "N/A"}
                          </span>
                          {sub.dob && (
                            <span className="user-email">DOB: {sub.dob}</span>
                          )}
                          {sub.rashi && (
                            <span className="user-email">
                              Rashi: {sub.rashi}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {imgUrl ? (
                          <img
                            src={imgUrl}
                            alt="Reference"
                            className="product-thumbnail"
                            style={{
                              cursor: "pointer",
                              width: "50px",
                              height: "50px",
                              objectFit: "cover",
                              borderRadius: "6px",
                              border: "1px solid #C9A227",
                            }}
                            onClick={() => setSelectedPodModal(sub)}
                            title="Click to view full image"
                          />
                        ) : (
                          <span style={{ fontSize: "0.82rem", color: "#888" }}>
                            No Image
                          </span>
                        )}
                      </td>
                      <td>
                        <select
                          className={`status-select ${sub.status || "pending"}`}
                          value={sub.status || "pending"}
                          onChange={(e) =>
                            handleUpdatePodStatus(sub.id, e.target.value)
                          }
                          style={{
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          <option value="pending">Pending</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="contacted">Contacted</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            className="options-btn"
                            style={{ padding: "5px 10px", fontSize: "0.82rem" }}
                            onClick={() => setSelectedPodModal(sub)}
                          >
                            View
                          </button>
                          <button
                            className="delete-btn"
                            style={{ padding: "5px 10px", fontSize: "0.82rem" }}
                            onClick={() => handleDeletePodSubmission(sub.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {podPagination && podPagination.pages > 1 && (
          <div className="pagination-controls">
            <button
              disabled={podPage <= 1 || loadingPod}
              onClick={() => setPodPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>
              Page {podPagination.page} of {podPagination.pages}
            </span>
            <button
              disabled={podPage >= podPagination.pages || loadingPod}
              onClick={() =>
                setPodPage((p) => Math.min(podPagination.pages, p + 1))
              }
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderBlogPanel = () => {
    return (
      <div className="panel-tab">
        {feedbackMessage && (
          <div className={`feedback-alert ${feedbackMessage.type}`}>
            {feedbackMessage.text}
          </div>
        )}
        <div className="catalog-grid-vertical animate-fade-in">
          {/* Blog Create / Edit Form */}
          <div className="catalog-form-box wide-form">
            <h3>{editingBlog ? "Edit Blog Post" : "Add New Blog Post"}</h3>
            <form onSubmit={handleSaveBlog} className="catalog-form grid-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="blog-title">Title *</label>
                  <input
                    id="blog-title"
                    type="text"
                    placeholder="e.g. 9 Mukhi Rudraksha: Benefits & Authenticity"
                    value={blogForm.title}
                    onChange={(e) =>
                      handleBlogFieldChange("title", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="blog-slug">Slug (Optional)</label>
                  <input
                    id="blog-slug"
                    type="text"
                    placeholder="e.g. 9-mukhi-rudraksha-benefits"
                    value={blogForm.slug}
                    onChange={(e) =>
                      handleBlogFieldChange("slug", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="blog-category">Category (Optional)</label>
                  <input
                    id="blog-category"
                    type="text"
                    placeholder="e.g. buyer-guide"
                    value={blogForm.category}
                    onChange={(e) =>
                      handleBlogFieldChange("category", e.target.value)
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="blog-author">Author (Optional)</label>
                  <input
                    id="blog-author"
                    type="text"
                    placeholder="e.g. Astro Logical Solutions"
                    value={blogForm.author}
                    onChange={(e) =>
                      handleBlogFieldChange("author", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="blog-image">Image URL (Optional)</label>
                  <input
                    id="blog-image"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={blogForm.image}
                    onChange={(e) =>
                      handleBlogFieldChange("image", e.target.value)
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="blog-status">Status</label>
                  <select
                    id="blog-status"
                    value={blogForm.status}
                    onChange={(e) =>
                      handleBlogFieldChange("status", e.target.value)
                    }
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              {/* <div className="form-group full-width-group">
              <label htmlFor="blog-excerpt">Excerpt / Short Description (Optional)</label>
              <textarea
                id="blog-excerpt"
                rows="2"
                placeholder="A short summary shown on blog listing cards..."
                value={blogForm.excerpt}
                onChange={(e) => handleBlogFieldChange("excerpt", e.target.value)}
              />
            </div>

            <div className="form-group full-width-group">
              <label htmlFor="blog-content">Content *</label>
              <textarea
                id="blog-content"
                rows="8"
                placeholder="Write the full blog article here..."
                value={blogForm.content}
                onChange={(e) => handleBlogFieldChange("content", e.target.value)}
                required
              />
            </div> */}

              <div className="form-group full-width-group blog-editor-group">
                <label htmlFor="blog-excerpt">
                  Excerpt / Short Description (Optional)
                </label>

                <CKEditor
                  key={`summary-${editingBlog?.id || "new"}`}
                  editor={ClassicEditor}
                  data={blogForm.excerpt || ""}
                  config={{
                    licenseKey: "GPL",

                    plugins: [
                      Essentials,
                      Paragraph,
                      Bold,
                      Italic,
                      Underline,
                      Strikethrough,
                      Heading,
                      List,
                      Link,
                      BlockQuote,
                      Table,
                      TableToolbar,
                      Alignment,
                    ],

                    toolbar: [
                      "undo",
                      "redo",
                      "|",
                      "heading",
                      "|",
                      "bold",
                      "italic",
                      "underline",
                      "strikethrough",
                      "|",
                      "link",
                      "bulletedList",
                      "numberedList",
                      "|",
                      "alignment",
                      "blockQuote",
                      "insertTable",
                    ],

                    heading: {
                      options: [
                        {
                          model: "paragraph",
                          title: "Paragraph",
                          class: "ck-heading_paragraph",
                        },
                        {
                          model: "heading2",
                          view: "h2",
                          title: "Heading 2",
                          class: "ck-heading_heading2",
                        },
                        {
                          model: "heading3",
                          view: "h3",
                          title: "Heading 3",
                          class: "ck-heading_heading3",
                        },
                        {
                          model: "heading4",
                          view: "h4",
                          title: "Heading 4",
                          class: "ck-heading_heading4",
                        },
                      ],
                    },

                    table: {
                      contentToolbar: [
                        "tableColumn",
                        "tableRow",
                        "mergeTableCells",
                      ],
                    },

                    placeholder: "Write a short blog summary...",
                  }}
                  onChange={(event, editor) => {
                    handleBlogFieldChange("excerpt", editor.getData());
                  }}
                />
              </div>
           <div className="form-group full-width-group blog-editor-group">
  <label>Content *</label>

  <CKEditor
    editor={ClassicEditor}
    data={blogForm.content || ""}
    config={{
      licenseKey: "GPL",
      plugins: [
        Essentials,
        Paragraph,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Heading,
        List,
        Link,
        BlockQuote,
        Table,
        TableToolbar,
        Alignment,
      ],
      toolbar: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "link",
        "bulletedList",
        "numberedList",
        "|",
        "alignment",
        "blockQuote",
        "insertTable",
      ],
      table: {
        contentToolbar: [
          "tableColumn",
          "tableRow",
          "mergeTableCells",
        ],
      },
    }}
    onChange={(event, editor) => {
      handleBlogFieldChange("content", editor.getData());
    }}
  />
</div>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="submit"
                  className="action-btn approve submit-product-btn"
                  disabled={savingBlog}
                >
                  {editingBlog ? <FaEdit /> : <FaPlus />}
                  {savingBlog
                    ? " Saving..."
                    : editingBlog
                      ? " Save Changes"
                      : " Publish Blog Post"}
                </button>
                {editingBlog && (
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={resetBlogForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Existing Blog Posts List */}
          <div className="catalog-list-box wide-list">
            <div className="panel-filters">
              <div className="search-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by title or category..."
                  value={blogSearchTerm}
                  onChange={(e) => {
                    setBlogSearchTerm(e.target.value);
                    setBlogsPage(1);
                  }}
                />
              </div>
              <div className="select-wrapper">
                <select
                  value={blogStatusFilter}
                  onChange={(e) => {
                    setBlogStatusFilter(e.target.value);
                    setBlogsPage(1);
                  }}
                >
                  <option value="ALL">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            {loadingBlogs ? (
              <div className="loading-state">Loading blog posts...</div>
            ) : blogs.length === 0 ? (
              <div className="empty-state">No blog posts found.</div>
            ) : (
              <div className="table-container">
                <table className="dashboard-table main-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Author</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.map((blog) => (
                      <tr key={blog.id}>
                        <td>
                          <div className="product-details-cell">
                            <span className="user-name">{blog.title}</span>
                            <span className="user-email">/{blog.slug}</span>
                          </div>
                        </td>
                        <td>{blog.category || "N/A"}</td>
                        <td>{blog.author || "N/A"}</td>
                        <td>
                          <span className={`status-badge ${blog.status}`}>
                            {blog.status}
                          </span>
                        </td>
                        <td>{new Date(blog.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="edit-btn"
                            onClick={() => handleEditBlogClick(blog)}
                          >
                            <FaEdit /> Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteBlog(blog.id)}
                          >
                            <FaTrash /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {blogsPagination && blogsPagination.pages > 1 && (
              <div className="pagination-controls">
                <button
                  disabled={blogsPage <= 1}
                  onClick={() => setBlogsPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span>
                  Page {blogsPagination.page} of {blogsPagination.pages}
                </span>
                <button
                  disabled={blogsPage >= blogsPagination.pages}
                  onClick={() =>
                    setBlogsPage((p) => Math.min(blogsPagination.pages, p + 1))
                  }
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCouponsPanel = () => {
    return (
      <div className="panel-tab">
        {feedbackMessage && (
          <div className={`feedback-alert ${feedbackMessage.type}`}>
            {feedbackMessage.text}
          </div>
        )}
        <div className="catalog-grid-vertical animate-fade-in">
          {/* Coupon Create / Edit Form */}
          <div className="catalog-form-box wide-form">
            <h3>{editingCoupon ? "Edit Coupon" : "Create New Coupon"}</h3>
            <form
              onSubmit={handleSaveCoupon}
              className="catalog-form grid-form"
            >
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="coupon-code">Code *</label>
                  <input
                    id="coupon-code"
                    type="text"
                    placeholder="e.g. SAVE20"
                    value={couponForm.code}
                    onChange={(e) =>
                      handleCouponFieldChange("code", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="coupon-discount-type">Discount Type *</label>
                  <select
                    id="coupon-discount-type"
                    value={couponForm.discountType}
                    onChange={(e) =>
                      handleCouponFieldChange("discountType", e.target.value)
                    }
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="coupon-discount-value">
                    {couponForm.discountType === "percentage"
                      ? "Discount %"
                      : "Discount Amount (₹)"}{" "}
                    *
                  </label>
                  <input
                    id="coupon-discount-value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={couponForm.discountValue}
                    onChange={(e) =>
                      handleCouponFieldChange("discountValue", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="coupon-max-discount">
                    Max Discount Cap (₹, Optional)
                  </label>
                  <input
                    id="coupon-max-discount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Only used for percentage discounts"
                    value={couponForm.maxDiscountAmount}
                    onChange={(e) =>
                      handleCouponFieldChange(
                        "maxDiscountAmount",
                        e.target.value,
                      )
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="coupon-min-order">
                    Minimum Order Amount (₹, Optional)
                  </label>
                  <input
                    id="coupon-min-order"
                    type="number"
                    min="0"
                    step="0.01"
                    value={couponForm.minOrderAmount}
                    onChange={(e) =>
                      handleCouponFieldChange("minOrderAmount", e.target.value)
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="coupon-expires">Expiry Date (Optional)</label>
                  <input
                    id="coupon-expires"
                    type="date"
                    value={couponForm.expiresAt}
                    onChange={(e) =>
                      handleCouponFieldChange("expiresAt", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-group full-width-group">
                <label htmlFor="coupon-description">
                  Description (Optional)
                </label>
                <textarea
                  id="coupon-description"
                  rows={2}
                  placeholder="Shown to shoppers alongside the coupon..."
                  value={couponForm.description}
                  onChange={(e) =>
                    handleCouponFieldChange("description", e.target.value)
                  }
                />
              </div>

              <div className="form-group full-width-group">
                <label htmlFor="coupon-applicability">Applies To *</label>
                <select
                  id="coupon-applicability"
                  value={couponForm.applicability}
                  onChange={(e) =>
                    handleCouponFieldChange("applicability", e.target.value)
                  }
                >
                  <option value="all">All Products</option>
                  <option value="category">Specific Categories</option>
                  <option value="product">Specific Products</option>
                </select>
              </div>

              {couponForm.applicability === "category" && (
                <div className="form-group full-width-group">
                  <label>Select Categories *</label>
                  <div className="scope-checkbox-list">
                    {categories.length === 0 ? (
                      <p className="empty-state">No categories found.</p>
                    ) : (
                      categories.map((cat) => (
                        <label key={cat.id} className="scope-checkbox-item">
                          <input
                            type="checkbox"
                            checked={couponForm.categoryIds.includes(cat.id)}
                            onChange={() =>
                              handleCouponScopeToggle("categoryIds", cat.id)
                            }
                          />
                          {cat.name}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {couponForm.applicability === "product" && (
                <div className="form-group full-width-group">
                  <label>Select Products *</label>
                  <div className="scope-checkbox-list">
                    {productsList.length === 0 ? (
                      <p className="empty-state">No products found.</p>
                    ) : (
                      productsList.map((prod) => (
                        <label key={prod.id} className="scope-checkbox-item">
                          <input
                            type="checkbox"
                            checked={couponForm.productIds.includes(prod.id)}
                            onChange={() =>
                              handleCouponScopeToggle("productIds", prod.id)
                            }
                          />
                          {prod.name}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="form-row">
                <label className="scope-checkbox-item">
                  <input
                    type="checkbox"
                    checked={couponForm.isSingleUse}
                    onChange={(e) =>
                      handleCouponFieldChange("isSingleUse", e.target.checked)
                    }
                  />
                  Single use only (once redeemed by anyone, it stops working)
                </label>

                <label className="scope-checkbox-item">
                  <input
                    type="checkbox"
                    checked={couponForm.requiresLogin}
                    onChange={(e) =>
                      handleCouponFieldChange("requiresLogin", e.target.checked)
                    }
                  />
                  Login reward (only for logged-in customers - OTP-verified ones
                  when mobile OTP is switched on; advertised in the "login to
                  get a discount" pop-up)
                </label>

                <label className="scope-checkbox-item">
                  <input
                    type="checkbox"
                    checked={couponForm.isActive}
                    onChange={(e) =>
                      handleCouponFieldChange("isActive", e.target.checked)
                    }
                  />
                  Active
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="action-btn approve submit-product-btn"
                  disabled={savingCoupon}
                >
                  {editingCoupon ? <FaEdit /> : <FaPlus />}
                  {savingCoupon
                    ? " Saving..."
                    : editingCoupon
                      ? " Save Changes"
                      : " Create Coupon"}
                </button>
                {editingCoupon && (
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={resetCouponForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Existing Coupons List */}
          <div className="catalog-list-box wide-list">
            <div className="panel-filters">
              <div className="search-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by code..."
                  value={couponSearchTerm}
                  onChange={(e) => {
                    setCouponSearchTerm(e.target.value);
                    setCouponsPage(1);
                  }}
                />
              </div>
              <div className="select-wrapper">
                <select
                  value={couponStatusFilter}
                  onChange={(e) => {
                    setCouponStatusFilter(e.target.value);
                    setCouponsPage(1);
                  }}
                >
                  <option value="ALL">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {loadingCoupons ? (
              <div className="loading-state">Loading coupons...</div>
            ) : coupons.length === 0 ? (
              <div className="empty-state">No coupons found.</div>
            ) : (
              <div className="table-container">
                <table className="dashboard-table main-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Discount</th>
                      <th>Applies To</th>
                      <th>Usage</th>
                      <th>Status</th>
                      <th>Expiry</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((coupon) => (
                      <tr key={coupon.id}>
                        <td>
                          <div className="product-details-cell">
                            <span className="user-name">{coupon.code}</span>
                            {coupon.description && (
                              <span className="user-email">
                                {coupon.description}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {coupon.discountType === "percentage"
                            ? `${coupon.discountValue}%`
                            : `₹${coupon.discountValue}`}
                        </td>
                        <td>
                          {coupon.applicability === "all"
                            ? "All Products"
                            : coupon.applicability === "category"
                              ? `Category (${coupon.categories?.length || 0})`
                              : `Product (${coupon.products?.length || 0})`}
                        </td>
                        <td>
                          {coupon.isSingleUse
                            ? coupon.usedCount > 0
                              ? "Used up (single-use)"
                              : "Single use"
                            : `${coupon.usedCount} used`}
                          {coupon.requiresLogin ? " · Login reward" : ""}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${coupon.isActive ? "active" : "draft"}`}
                          >
                            {coupon.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          {coupon.expiresAt
                            ? new Date(coupon.expiresAt).toLocaleDateString()
                            : "No expiry"}
                        </td>
                        <td>
                          <button
                            className="edit-btn"
                            onClick={() => handleEditCouponClick(coupon)}
                          >
                            <FaEdit /> Edit
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                          >
                            <FaTrash /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {couponsPagination && couponsPagination.pages > 1 && (
              <div className="pagination-controls">
                <button
                  disabled={couponsPage <= 1}
                  onClick={() => setCouponsPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span>
                  Page {couponsPagination.page} of {couponsPagination.pages}
                </span>
                <button
                  disabled={couponsPage >= couponsPagination.pages}
                  onClick={() =>
                    setCouponsPage((p) =>
                      Math.min(couponsPagination.pages, p + 1),
                    )
                  }
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderReviewsPanel = () => {
    return (
      <div className="panel-tab">
        {feedbackMessage && (
          <div className={`feedback-alert ${feedbackMessage.type}`}>
            {feedbackMessage.text}
          </div>
        )}
        <div className="catalog-list-box wide-list">
          <div className="panel-filters">
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by review message..."
                value={reviewSearchTerm}
                onChange={(e) => {
                  setReviewSearchTerm(e.target.value);
                  setReviewsPage(1);
                }}
              />
            </div>
            <div className="select-wrapper">
              <select
                value={reviewRatingFilter}
                onChange={(e) => {
                  setReviewRatingFilter(e.target.value);
                  setReviewsPage(1);
                }}
              >
                <option value="ALL">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>
          </div>

          {loadingReviews ? (
            <div className="loading-state">Loading reviews...</div>
          ) : reviews.length === 0 ? (
            <div className="empty-state">No reviews found.</div>
          ) : (
            <div className="table-container">
              <table className="dashboard-table main-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Reviewer</th>
                    <th>Rating</th>
                    <th>Message</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review) => (
                    <tr key={review.id}>
                      <td>
                        <div className="product-details-cell">
                          <span className="user-name">
                            {review.product?.name || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="product-details-cell">
                          <span className="user-name">
                            {review.user?.name || "N/A"}
                          </span>
                          <span className="user-email">
                            {review.user?.email}
                          </span>
                        </div>
                      </td>
                      <td>
                        {"★".repeat(review.rating)}
                        {"☆".repeat(5 - review.rating)}
                      </td>
                      <td>{review.comment}</td>
                      <td>{new Date(review.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteReview(review.id)}
                        >
                          <FaTrash /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {reviewsPagination && reviewsPagination.pages > 1 && (
            <div className="pagination-controls">
              <button
                disabled={reviewsPage <= 1}
                onClick={() => setReviewsPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span>
                Page {reviewsPagination.page} of {reviewsPagination.pages}
              </span>
              <button
                disabled={reviewsPage >= reviewsPagination.pages}
                onClick={() =>
                  setReviewsPage((p) =>
                    Math.min(reviewsPagination.pages, p + 1),
                  )
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCategoriesSubTab = () => {
    const isSubForm = catFormMode === "subcategory";
    const isEditing = !!editingCategory;
    const subcategoryTotal = categories.reduce(
      (sum, cat) => sum + (cat.subcategories || []).length,
      0,
    );

    // Search matches a category, or any of its subcategories
    const term = catSearch.trim().toLowerCase();
    const matches = (item) =>
      item.name.toLowerCase().includes(term) ||
      (item.slug || "").toLowerCase().includes(term);
    const visibleRows = [];
    categories.forEach((cat) => {
      const subs = cat.subcategories || [];
      if (!term || matches(cat)) {
        visibleRows.push({ cat, subs });
        return;
      }
      const matchingSubs = subs.filter(matches);
      if (matchingSubs.length > 0) visibleRows.push({ cat, subs: matchingSubs });
    });

    const closeMenu = (e) =>
      e.currentTarget.closest("details")?.removeAttribute("open");

    const renderStatus = (item) =>
      item.isActive === false ? (
        <span className="inv-state hidden">Hidden</span>
      ) : (
        <span className="inv-state active">
          <span className="inv-dot" /> Visible
        </span>
      );

    const renderRowActions = (item, isSub, parent) => (
      <div className="inv-actions">
        <button
          type="button"
          className="edit-btn"
          onClick={() =>
            isSub
              ? handleEditSubcategoryClick(item, parent.id)
              : handleEditCategoryClick(item)
          }
        >
          <FaEdit /> Edit
        </button>
        <details className="row-actions">
          <summary>Actions</summary>
          <div className="row-actions-menu">
            {!isSub && (
              <button
                type="button"
                onClick={(e) => {
                  closeMenu(e);
                  openCreateCategoryForm("subcategory", item.id);
                }}
              >
                Add subcategory
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                closeMenu(e);
                handleToggleVisibility(item, isSub);
              }}
            >
              {item.isActive === false ? "Show on website" : "Hide from website"}
            </button>
            <button
              type="button"
              className="danger"
              onClick={(e) => {
                closeMenu(e);
                if (isSub) handleDeleteSubcategory(item.id);
                else handleDeleteCategory(item.id);
              }}
            >
              Delete
            </button>
          </div>
        </details>
      </div>
    );

    return (
      <div className="catalog-grid-vertical animate-fade-in">
        {/* Toolbar: category and subcategory are created separately */}
        <div className="cat-toolbar">
          <button
            type="button"
            className="action-btn approve"
            onClick={() => openCreateCategoryForm("category")}
          >
            <FaPlus /> New Category
          </button>
          <button
            type="button"
            className="action-btn approve cat-toolbar-secondary"
            onClick={() => openCreateCategoryForm("subcategory")}
            disabled={categories.length === 0}
            title={
              categories.length === 0
                ? "Create a category first"
                : "Add a subcategory under an existing category"
            }
          >
            <FaPlus /> New Subcategory
          </button>
          <div className="search-wrapper cat-toolbar-search">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search categories and subcategories..."
              value={catSearch}
              onChange={(e) => setCatSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Dedicated form: one for categories, one for subcategories */}
        {catFormMode && (
          <div className="catalog-form-box cat-form-card" id="cat-form-card">
            <h3>
              {isEditing ? "Edit" : "Create"}{" "}
              {isSubForm ? "Subcategory" : "Category"}
            </h3>
            <p className="cat-form-help">
              {isSubForm
                ? "A subcategory sits inside a category, e.g. “Rudraksha Malas” inside “Rudrakshas”."
                : "A category is a top-level section of the shop, e.g. “Rudrakshas” or “Gemstones”."}
            </p>
            <form onSubmit={handleCreateCategory} className="catalog-form">
              {isSubForm && (
                <div className="form-group">
                  <label htmlFor="cat-parent">Parent Category *</label>
                  <select
                    id="cat-parent"
                    value={catParentId}
                    onChange={(e) => setCatParentId(e.target.value)}
                    required
                  >
                    <option value="">Select parent category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row-2">
                <div className="form-group">
                  <label htmlFor="cat-name">
                    {isSubForm ? "Subcategory" : "Category"} Name *
                  </label>
                  <input
                    id="cat-name"
                    type="text"
                    placeholder={
                      isSubForm ? "e.g. Rudraksha Malas" : "e.g. Rudrakshas"
                    }
                    value={catName}
                    onChange={(e) => setCatName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cat-slug">
                    Slug {isEditing ? "" : "(Optional)"}
                  </label>
                  <input
                    id="cat-slug"
                    type="text"
                    placeholder={
                      isSubForm ? "e.g. rudraksha-malas" : "e.g. rudrakshas"
                    }
                    value={catSlug}
                    onChange={(e) => setCatSlug(e.target.value)}
                  />
                </div>
              </div>

              {isSubForm && (
                <div className="form-group">
                  <label htmlFor="cat-image">Subcategory Image (Optional)</label>
                  {(catImagePreview ||
                    (catExistingImage && !catRemoveImage)) && (
                    <div className="cat-image-preview">
                      <img
                        src={
                          catImagePreview ||
                          getUploadedImageSrc(catExistingImage)
                        }
                        alt="Subcategory preview"
                      />
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => {
                          if (catImagePreview) {
                            URL.revokeObjectURL(catImagePreview);
                            setCatImageFile(null);
                            setCatImagePreview("");
                          } else {
                            setCatRemoveImage(true);
                          }
                          const input = document.getElementById("cat-image");
                          if (input) input.value = "";
                        }}
                      >
                        <FaTrash /> Remove
                      </button>
                    </div>
                  )}
                  {catRemoveImage && (
                    <p className="cat-image-note">
                      The current image will be removed when you save.
                    </p>
                  )}
                  <input
                    id="cat-image"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleCategoryImageChange}
                  />
                  <small className="cat-image-note">
                    JPEG, PNG or WebP, up to 5 MB.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="cat-desc">Description</label>
                <textarea
                  id="cat-desc"
                  rows="3"
                  placeholder={`Describe this ${isSubForm ? "subcategory" : "category"}...`}
                  value={catDescription}
                  onChange={(e) => setCatDescription(e.target.value)}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="action-btn approve">
                  {isEditing ? <FaEdit /> : <FaPlus />}
                  {isEditing
                    ? " Save Changes"
                    : ` Create ${isSubForm ? "Subcategory" : "Category"}`}
                </button>
                <button
                  type="button"
                  className="delete-btn"
                  onClick={resetCategoryForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories & subcategories list */}
        <div className="catalog-panel">
          <div className="box-header flex-header">
            <h3>Categories Structure</h3>
            <div className="inventory-summary">
              <strong>{categories.length}</strong> categories ·{" "}
              <strong>{subcategoryTotal}</strong> subcategories
            </div>
          </div>

          {loadingCategories ? (
            <div className="loading-state">Loading categories...</div>
          ) : categories.length === 0 ? (
            <div className="empty-state">
              No categories created yet. Use “New Category” to add the first
              one.
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="empty-state">
              No categories or subcategories match your search.
            </div>
          ) : (
            <div className="catalog-table-wrap">
              <table className="dashboard-table inv-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Details</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(({ cat, subs }) => (
                    <React.Fragment key={cat.id}>
                      <tr className="cat-row-parent">
                        <td>
                          <div className="inv-title-cell">
                            <span className="inv-thumb-icon">
                              <FaFolderOpen />
                            </span>
                            <div className="inv-title-text">
                              <span className="inv-title">{cat.name}</span>
                              <span className="inv-sub">/{cat.slug}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="inv-type category">Category</span>
                        </td>
                        <td>
                          <div className="inv-details">
                            <span>
                              {(cat.subcategories || []).length} subcategories
                            </span>
                            {cat.description && (
                              <span className="inv-sub">{cat.description}</span>
                            )}
                          </div>
                        </td>
                        <td>{renderStatus(cat)}</td>
                        <td>{renderRowActions(cat, false)}</td>
                      </tr>

                      {subs.map((sub) => (
                        <tr key={sub.id} className="cat-row-child">
                          <td>
                            <div className="inv-title-cell indent">
                              <span className="inv-branch">↳</span>
                              {sub.imageUrl ? (
                                <img
                                  className="inv-thumb round"
                                  src={getUploadedImageSrc(sub.imageUrl)}
                                  alt={sub.name}
                                />
                              ) : (
                                <span className="inv-thumb-icon sub">
                                  <FaFolderOpen />
                                </span>
                              )}
                              <div className="inv-title-text">
                                <span className="inv-title">{sub.name}</span>
                                <span className="inv-sub">/{sub.slug}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="inv-type subcategory">
                              Subcategory
                            </span>
                          </td>
                          <td>
                            <div className="inv-details">
                              <span>In {cat.name}</span>
                              {sub.description && (
                                <span className="inv-sub">
                                  {sub.description}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{renderStatus(sub)}</td>
                          <td>{renderRowActions(sub, true, cat)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProductsSubTab = () => {
    return (
      <div className="catalog-grid-vertical animate-fade-in">
        {/* Product Add Form */}
        <div className="catalog-form-box wide-form">
          <h3>Add New Product</h3>
          <form
            onSubmit={handleCreateProduct}
            className="catalog-form grid-form"
          >
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prod-category">Category *</label>
                <select
                  id="prod-category"
                  value={prodCategoryId}
                  onChange={(e) => {
                    setProdCategoryId(e.target.value);
                    setProdSubcategoryId("");
                  }}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="prod-subcategory">Subcategory (Optional)</label>
                <select
                  id="prod-subcategory"
                  value={prodSubcategoryId}
                  onChange={(e) => setProdSubcategoryId(e.target.value)}
                  disabled={!prodCategoryId}
                >
                  <option value="">None (Top-Level Category Only)</option>
                  {prodCategoryId &&
                    (
                      categories.find((c) => c.id === prodCategoryId)
                        ?.subcategories || []
                    ).map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="prod-name">Product Name *</label>
                <input
                  id="prod-name"
                  type="text"
                  placeholder="e.g. 5 Mukhi Rudraksha Bead"
                  value={prodName}
                  onChange={(e) => setProdName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prod-price">Price (INR) *</label>
                <input
                  id="prod-price"
                  type="number"
                  step="0.01"
                  placeholder="e.g. 499.00"
                  value={prodPrice}
                  onChange={(e) => setProdPrice(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="prod-slug">Slug (Optional)</label>
                <input
                  id="prod-slug"
                  type="text"
                  placeholder="e.g. 5-mukhi-rudraksha-bead"
                  value={prodSlug}
                  onChange={(e) => setProdSlug(e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prod-mukhi">Mukhi Count (Optional)</label>
                <input
                  id="prod-mukhi"
                  type="number"
                  placeholder="e.g. 5"
                  value={prodMukhiCount}
                  onChange={(e) => setProdMukhiCount(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="prod-origin">Origin (Optional)</label>
                <input
                  id="prod-origin"
                  type="text"
                  placeholder="e.g. Nepal / Java"
                  value={prodOrigin}
                  onChange={(e) => setProdOrigin(e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prod-size">Bead Size (mm) (Optional)</label>
                <input
                  id="prod-size"
                  type="text"
                  placeholder="e.g. 18mm"
                  value={prodBeadSizeMm}
                  onChange={(e) => setProdBeadSizeMm(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="prod-stock">Stock Quantity *</label>
                <input
                  id="prod-stock"
                  type="number"
                  placeholder="e.g. 50"
                  value={prodStockQuantity}
                  onChange={(e) => setProdStockQuantity(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prod-status">Status</label>
                <select
                  id="prod-status"
                  value={prodStatus}
                  onChange={(e) => setProdStatus(e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="product-images-input">
                  Upload Images (Max 10 files) *
                </label>
                <input
                  id="product-images-input"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setProdImages(Array.from(e.target.files))}
                />
                <span className="file-hint">
                  Select JPEG, PNG, or WebP files
                </span>
              </div>
            </div>

            <div className="form-row certification-row">
              <div className="form-group checkbox-group">
                <label className="checkbox-label" htmlFor="prod-certified">
                  <input
                    id="prod-certified"
                    type="checkbox"
                    checked={prodIsCertified}
                    onChange={(e) => setProdIsCertified(e.target.checked)}
                  />
                  Is Lab Certified?
                </label>
              </div>

              {prodIsCertified && (
                <div className="form-group">
                  <label htmlFor="prod-cert-url">
                    Certification Report URL
                  </label>
                  <input
                    id="prod-cert-url"
                    type="url"
                    placeholder="https://example.com/certificate.pdf"
                    value={prodCertificationUrl}
                    onChange={(e) => setProdCertificationUrl(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* <div className="form-group full-width-group">
              <label htmlFor="prod-desc">Product Description</label>
              <textarea
                id="prod-desc"
                rows="4"
                placeholder="Write full product details and benefits..."
                value={prodDescription}
                onChange={(e) => setProdDescription(e.target.value)}
              />
            </div> */}
            <div className="form-group full-width-group blog-editor-group">
  <label>Product Description</label>

  <CKEditor
    editor={ClassicEditor}
    data={prodDescription || ""}
    config={{
      licenseKey: "GPL",

      plugins: [
        Essentials,
        Paragraph,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Heading,
        List,
        Link,
        BlockQuote,
        Table,
        TableToolbar,
        Alignment,
      ],

      toolbar: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "link",
        "bulletedList",
        "numberedList",
        "|",
        "alignment",
        "blockQuote",
        "insertTable",
      ],

      heading: {
        options: [
          {
            model: "paragraph",
            title: "Paragraph",
            class: "ck-heading_paragraph",
          },
          {
            model: "heading2",
            view: "h2",
            title: "Heading 2",
            class: "ck-heading_heading2",
          },
          {
            model: "heading3",
            view: "h3",
            title: "Heading 3",
            class: "ck-heading_heading3",
          },
          {
            model: "heading4",
            view: "h4",
            title: "Heading 4",
            class: "ck-heading_heading4",
          },
        ],
      },

      table: {
        contentToolbar: [
          "tableColumn",
          "tableRow",
          "mergeTableCells",
        ],
      },

      placeholder: "Write full product details and benefits...",
    }}
    onChange={(event, editor) => {
      setProdDescription(editor.getData());
    }}
  />
</div>

            <button
              type="submit"
              className="action-btn approve submit-product-btn"
            >
              <FaPlus /> Add Product & Upload Images
            </button>
          </form>
        </div>

        {/* Existing Products List */}
        <div className="catalog-panel wide-list">
          <div className="box-header flex-header">
            <h3>Products Inventory</h3>
            <div className="inventory-summary">
              Total Products: <strong>{productsTotal}</strong>
            </div>
          </div>

          <div className="inv-toolbar">
            <div className="search-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search name, description, category, origin..."
                value={inventorySearch}
                onChange={(e) => {
                  setInventorySearch(e.target.value);
                  setInventoryPage(1);
                }}
              />
            </div>
            <div className="inv-chips" role="group" aria-label="Filter by status">
              {[
                ["", "All"],
                ["active", "Active"],
                ["draft", "Draft"],
                ["out_of_stock", "Out of stock"],
              ].map(([value, label]) => (
                <button
                  key={value || "all"}
                  type="button"
                  className={`inv-chip ${inventoryStatusFilter === value ? "active" : ""}`}
                  onClick={() => {
                    setInventoryStatusFilter(value);
                    setInventoryPage(1);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {loadingInventory && inventoryProducts.length === 0 ? (
            <div className="loading-state">Loading products...</div>
          ) : inventoryProducts.length === 0 ? (
            <div className="empty-state">
              {inventorySearch.trim() || inventoryStatusFilter
                ? "No products match these filters."
                : "No products found in database."}
            </div>
          ) : (
            <div className="catalog-table-wrap">
              <table className="dashboard-table inv-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Inventory</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryProducts.map((prod) => {
                    const imgUrl = getProductImageUrl(prod);
                    const categoryObj = categories.find(
                      (c) => c.id === prod.categoryId,
                    );
                    const subObj =
                      prod.subcategory ||
                      categoryObj?.subcategories?.find(
                        (s) => s.id === prod.subcategoryId,
                      );
                    const stock = Number(prod.stockQuantity) || 0;
                    const stockClass =
                      prod.status === "out_of_stock" || stock <= 0
                        ? "out"
                        : stock <= 5
                          ? "low"
                          : "ok";
                    const stockLabel =
                      stockClass === "out"
                        ? "Out of stock"
                        : stockClass === "low"
                          ? `Low stock: ${stock} left`
                          : `${stock} in stock`;
                    const specs = [
                      prod.mukhiCount ? `${prod.mukhiCount} Mukhi` : null,
                      prod.beadSizeMm,
                      prod.origin,
                    ].filter(Boolean);
                    const closeMenu = (e) =>
                      e.currentTarget
                        .closest("details")
                        ?.removeAttribute("open");

                    return (
                      <tr key={prod.id}>
                        <td>
                          <div className="inv-title-cell">
                            {imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={prod.name}
                                className="inv-thumb"
                              />
                            ) : (
                              <div className="inv-thumb inv-thumb-empty">
                                <FaFileImage />
                              </div>
                            )}
                            <div className="inv-title-text">
                              <span className="inv-title">{prod.name}</span>
                              <span className={`inv-state ${prod.status}`}>
                                {prod.status === "active" && (
                                  <span className="inv-dot" />
                                )}
                                {prod.status.replace("_", " ")}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="inv-details">
                            <span>{categoryObj?.name || "Uncategorized"}</span>
                            {subObj && (
                              <span className="inv-sub">{subObj.name}</span>
                            )}
                            {(specs.length > 0 || prod.isCertified) && (
                              <span className="inv-sub">
                                {specs.join(" · ")}
                                {prod.isCertified &&
                                  `${specs.length ? " · " : ""}Certified`}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="font-semibold">
                          ₹{parseFloat(prod.price).toFixed(2)}
                        </td>
                        <td>
                          <span className={`inv-stock ${stockClass}`}>
                            {stockLabel}
                          </span>
                        </td>
                        <td>
                          <div className="inv-actions">
                            <button
                              className="edit-btn"
                              onClick={() => openEditProduct(prod)}
                            >
                              <FaEdit /> Edit
                            </button>
                            <details className="row-actions">
                              <summary>Actions</summary>
                              <div className="row-actions-menu">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    closeMenu(e);
                                    openOptionsPanel(prod);
                                  }}
                                >
                                  Options
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    closeMenu(e);
                                    setFaqPanelProduct(prod);
                                  }}
                                >
                                  FAQs
                                </button>
                                <button
                                  type="button"
                                  className="danger"
                                  onClick={(e) => {
                                    closeMenu(e);
                                    handleDeleteProduct(prod.id);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </details>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {inventoryPagination && inventoryPagination.pages > 1 && (
            <div className="pagination-controls">
              <button
                disabled={inventoryPage <= 1 || loadingInventory}
                onClick={() => setInventoryPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span>
                Page {inventoryPagination.page} of {inventoryPagination.pages}
              </span>
              <button
                disabled={
                  inventoryPage >= inventoryPagination.pages || loadingInventory
                }
                onClick={() =>
                  setInventoryPage((p) =>
                    Math.min(inventoryPagination.pages, p + 1),
                  )
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCatalogPanel = () => {
    return (
      <div className="panel-tab catalog-tab-container">
        <div className="catalog-subtab-headers">
          <button
            className={`catalog-subtab-btn ${catalogSubTab === "products" ? "active" : ""}`}
            onClick={() => setCatalogSubTab("products")}
          >
            <FaBoxes /> Products ({productsTotal})
          </button>
          <button
            className={`catalog-subtab-btn ${catalogSubTab === "categories" ? "active" : ""}`}
            onClick={() => setCatalogSubTab("categories")}
          >
            <FaFolderOpen /> Categories & Subcategories ({categories.length})
          </button>
        </div>

        {feedbackMessage && (
          <div className={`feedback-alert ${feedbackMessage.type}`}>
            {feedbackMessage.text}
          </div>
        )}

        {catalogSubTab === "products"
          ? renderProductsSubTab()
          : renderCategoriesSubTab()}
      </div>
    );
  };

  if (!adminUser) return null;

  return (
    <div className="admin-dashboard-container">
      {/* Sidebar Navigation */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo">
          <span className="logo-icon">✦</span>
          <h2>Rudraksha Admin</h2>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("dashboard");
              setSearchTerm("");
              setRoleFilter("ALL");
              setOrderStatusFilter("ALL");
              setUsersPage(1);
              setOrdersPage(1);
            }}
          >
            <FaChartLine /> Dashboard Overview
          </button>
          <button
            className={`nav-item ${activeTab === "users" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("users");
              setSearchTerm("");
              setRoleFilter("ALL");
              setUsersPage(1);
            }}
          >
            <FaUsers /> Manage Users
          </button>
          <button
            className={`nav-item ${activeTab === "astrologers" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("astrologers");
              setSearchTerm("");
              setStatusFilter("ALL");
            }}
          >
            <FaUserAstronaut /> Astrologers
            {pendingAstrologersCount > 0 && (
              <span className="badge-count">{pendingAstrologersCount}</span>
            )}
          </button>
          <button
            className={`nav-item ${activeTab === "catalog" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("catalog");
              setSearchTerm("");
            }}
          >
            <FaBoxes /> Products & Categories
          </button>
          <button
            className={`nav-item ${activeTab === "orders" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("orders");
              setSearchTerm("");
              setOrderStatusFilter("ALL");
              setOrdersPage(1);
            }}
          >
            <FaShoppingBag /> Orders & Sales
          </button>
          <button
            className={`nav-item ${activeTab === "contact" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("contact");
              setContactsPage(1);
            }}
          >
            <FaEnvelope /> Contact Submissions
          </button>
          <button
            className={`nav-item ${activeTab === "blog" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("blog");
              setBlogSearchTerm("");
              setBlogStatusFilter("ALL");
              setBlogsPage(1);
              resetBlogForm();
            }}
          >
            <FaBlog /> Blog
          </button>
          <button
            className={`nav-item ${activeTab === "coupons" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("coupons");
              setCouponSearchTerm("");
              setCouponStatusFilter("ALL");
              setCouponsPage(1);
              resetCouponForm();
            }}
          >
            <FaTag /> Coupons
          </button>
          <button
            className={`nav-item ${activeTab === "reviews" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("reviews");
              setReviewSearchTerm("");
              setReviewRatingFilter("ALL");
              setReviewsPage(1);
            }}
          >
            <FaStar /> Product Reviews
          </button>
          <button
            className={`nav-item ${activeTab === "vaidikvidhya" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("vaidikvidhya");
              setVaidikPage(1);
            }}
          >
            <GiPrayerBeads /> Vaidik Vidhya Submissions
          </button>
          <button
            className={`nav-item ${activeTab === "pod" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("pod");
              setPodPage(1);
              setPodSearchTerm("");
              setPodStatusFilter("ALL");
            }}
          >
            <FaBoxes /> Product On Demand
          </button>
          <button
            className={`nav-item ${activeTab === "consultations" ? "active" : ""}`}
            onClick={() => setActiveTab("consultations")}
          >
            <FaUserAstronaut /> Consultations
          </button>
          <button
            className={`nav-item ${activeTab === "announcements" ? "active" : ""}`}
            onClick={() => setActiveTab("announcements")}
          >
            <FaTag /> Offers Bar
          </button>
          <button
            className={`nav-item ${activeTab === "checkoutsettings" ? "active" : ""}`}
            onClick={() => setActiveTab("checkoutsettings")}
          >
            <FaShoppingBag /> Checkout Charges
          </button>
          <button
            className={`nav-item ${activeTab === "numerology" ? "active" : ""}`}
            onClick={() => setActiveTab("numerology")}
          >
            <FaStar /> Mobile Numerology
          </button>
          <button
            className={`nav-item ${activeTab === "rudraksha" ? "active" : ""}`}
            onClick={() => setActiveTab("rudraksha")}
          >
            <GiPrayerBeads /> Rudraksha Calculator
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="admin-profile">
            <div className="admin-avatar">
              {adminUser.name ? adminUser.name.charAt(0).toUpperCase() : "A"}
            </div>
            <div className="admin-meta-info">
              <span className="admin-name">{adminUser.name}</span>
              <span className="admin-role">Super Admin</span>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <FaSignOutAlt /> Log Out
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="dashboard-main-content">
        <header className="dashboard-topbar">
          <div className="topbar-left">
            <h1>
              {activeTab === "dashboard" && "Dashboard Overview"}
              {activeTab === "users" && "Manage Users"}
              {activeTab === "astrologers" &&
                "Astrologer Verification & Management"}
              {activeTab === "catalog" && "Product & Category Catalog"}
              {activeTab === "orders" && "Orders & Transactions"}
              {activeTab === "contact" && "Contact Form Submissions"}
              {activeTab === "blog" && "Blog Management"}
              {activeTab === "coupons" && "Coupon Management"}
              {activeTab === "reviews" && "Product Reviews"}
              {activeTab === "vaidikvidhya" && "Vaidik Vidhya Submissions"}
              {activeTab === "pod" && "Product On Demand Submissions"}
              {activeTab === "consultations" && "Consultations"}
              {activeTab === "announcements" && "Header Offers Bar"}
              {activeTab === "checkoutsettings" && "Checkout Charges"}
              {activeTab === "numerology" && "Mobile Numerology"}
              {activeTab === "rudraksha" && "Rudraksha Calculator"}
            </h1>
            <p className="topbar-subtitle">
              Celestial administrative control console
            </p>
          </div>
          <div className="topbar-right">
            <div className="system-status">
              <span className="status-dot"></span> System Live
            </div>
          </div>
        </header>

        <div className="dashboard-content-body">
          {activeTab === "dashboard" && renderDashboardOverview()}
          {activeTab === "users" && renderUsersPanel()}
          {activeTab === "astrologers" && renderAstrologersPanel()}
          {activeTab === "catalog" && renderCatalogPanel()}
          {activeTab === "orders" && renderOrdersPanel()}
          {activeTab === "contact" && renderContactPanel()}
          {activeTab === "blog" && renderBlogPanel()}
          {activeTab === "coupons" && renderCouponsPanel()}
          {activeTab === "reviews" && renderReviewsPanel()}
          {activeTab === "vaidikvidhya" && renderVaidikVidhyaPanel()}
          {activeTab === "pod" && renderProductOnDemandPanel()}
          {activeTab === "consultations" && (
            <AdminConsultations token={localStorage.getItem("adminToken")} />
          )}
          {activeTab === "announcements" && (
            <AdminAnnouncements token={localStorage.getItem("adminToken")} />
          )}
          {activeTab === "checkoutsettings" && (
            <AdminCheckoutSettings token={localStorage.getItem("adminToken")} />
          )}
          {activeTab === "numerology" && (
            <AdminMobileNumerology token={localStorage.getItem("adminToken")} />
          )}
          {activeTab === "rudraksha" && (
            <AdminRudraksha token={localStorage.getItem("adminToken")} />
          )}
        </div>
      </main>

      {faqPanelProduct && (
        <AdminProductFaqs
          product={faqPanelProduct}
          onClose={() => setFaqPanelProduct(null)}
          showFeedback={showFeedback}
        />
      )}

      {optionsPanelProduct && (
        <div className="options-modal-overlay" onClick={closeOptionsPanel}>
          <div className="options-modal" onClick={(e) => e.stopPropagation()}>
            <div className="options-modal-header">
              <h3>Options — {optionsPanelProduct.name}</h3>
              <button
                className="options-modal-close"
                onClick={closeOptionsPanel}
              >
                &times;
              </button>
            </div>

            {loadingProductOptions ? (
              <p>Loading option groups...</p>
            ) : (
              <div className="options-group-list">
                {productOptionGroups.length === 0 && (
                  <p>No option groups yet for this product.</p>
                )}

                {productOptionGroups.map((group) => (
                  <div key={group.id} className="options-group-card">
                    <div className="options-group-card-header">
                      <strong>
                        {group.name} ({group.selectionType}
                        {group.isRequired ? ", required" : ""})
                      </strong>
                      <div>
                        <button
                          type="button"
                          onClick={() => loadGroupIntoForm(group)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="delete-btn"
                          onClick={() => handleDeleteGroup(group.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <ul>
                      {group.choices.map((choice) => (
                        <li key={choice.id}>
                          {choice.label} — +₹
                          {Number(choice.priceDelta).toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <form className="options-group-form" onSubmit={handleSaveGroup}>
              <h4>{groupForm.id ? "Edit Group" : "Add New Group"}</h4>

              <input
                type="text"
                placeholder="Group name (e.g. Metal)"
                value={groupForm.name}
                onChange={(e) =>
                  setGroupForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />

              <div className="options-group-form-row">
                <select
                  value={groupForm.selectionType}
                  onChange={(e) =>
                    setGroupForm((prev) => ({
                      ...prev,
                      selectionType: e.target.value,
                    }))
                  }
                >
                  <option value="single">Single choice (radio)</option>
                  <option value="multiple">Multiple choice (checkbox)</option>
                </select>

                <label>
                  <input
                    type="checkbox"
                    checked={groupForm.isRequired}
                    onChange={(e) =>
                      setGroupForm((prev) => ({
                        ...prev,
                        isRequired: e.target.checked,
                      }))
                    }
                  />
                  Required
                </label>
              </div>

              <p className="options-hint">
                Each choice is one complete option with its full price. To build
                steps, separate them with " › ", for example{" "}
                <strong>Ring › Silver › RD1</strong>, and name the group the
                same way (<strong>Style › Metal › Design</strong>); the product
                page then shows the steps one by one.
              </p>

              <h5>Show only when (optional)</h5>
              <div className="options-group-form-row">
                <input
                  type="text"
                  placeholder="Group (e.g. Style › Metal › Design)"
                  value={groupForm.showWhenGroup}
                  onChange={(e) =>
                    setGroupForm((prev) => ({
                      ...prev,
                      showWhenGroup: e.target.value,
                    }))
                  }
                />
                <input
                  type="text"
                  placeholder="Selected choice starts with (e.g. Ring)"
                  value={groupForm.showWhenPath}
                  onChange={(e) =>
                    setGroupForm((prev) => ({
                      ...prev,
                      showWhenPath: e.target.value,
                    }))
                  }
                />
              </div>

              <h5>Choices</h5>
              {groupForm.choices.map((choice, index) => (
                <div key={index} className="options-choice-row">
                  <input
                    type="text"
                    placeholder="Label (e.g. Copper)"
                    value={choice.label}
                    onChange={(e) =>
                      handleChoiceFieldChange(index, "label", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    placeholder="Price +₹"
                    value={choice.priceDelta}
                    onChange={(e) =>
                      handleChoiceFieldChange(
                        index,
                        "priceDelta",
                        e.target.value,
                      )
                    }
                  />
                  <input
                    type="text"
                    placeholder="Image URL (optional)"
                    value={choice.imageUrl}
                    onChange={(e) =>
                      handleChoiceFieldChange(index, "imageUrl", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveChoiceRow(index)}
                  >
                    &times;
                  </button>
                </div>
              ))}

              <button type="button" onClick={handleAddChoiceRow}>
                + Add Choice
              </button>

              <div className="options-group-form-actions">
                <button type="submit" disabled={savingGroup}>
                  {savingGroup
                    ? "Saving..."
                    : groupForm.id
                      ? "Update Group"
                      : "Create Group"}
                </button>
                {groupForm.id && (
                  <button
                    type="button"
                    onClick={() => setGroupForm(emptyGroupForm)}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {editingProduct && editProductForm && (
        <div className="options-modal-overlay" onClick={closeEditProduct}>
          <div className="options-modal" onClick={(e) => e.stopPropagation()}>
            <div className="options-modal-header">
              <h3>Edit Product — {editingProduct.name}</h3>
              <button
                className="options-modal-close"
                onClick={closeEditProduct}
              >
                &times;
              </button>
            </div>

            <form
              onSubmit={handleUpdateProduct}
              className="catalog-form grid-form"
            >
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-prod-category">Category *</label>
                  <select
                    id="edit-prod-category"
                    value={editProductForm.categoryId}
                    onChange={(e) => {
                      handleEditProductFieldChange(
                        "categoryId",
                        e.target.value,
                      );
                      handleEditProductFieldChange("subcategoryId", "");
                    }}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-prod-subcategory">
                    Subcategory (Optional)
                  </label>
                  <select
                    id="edit-prod-subcategory"
                    value={editProductForm.subcategoryId}
                    onChange={(e) =>
                      handleEditProductFieldChange(
                        "subcategoryId",
                        e.target.value,
                      )
                    }
                    disabled={!editProductForm.categoryId}
                  >
                    <option value="">None (Top-Level Category Only)</option>
                    {editProductForm.categoryId &&
                      (
                        categories.find(
                          (c) => c.id === editProductForm.categoryId,
                        )?.subcategories || []
                      ).map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-prod-name">Product Name *</label>
                  <input
                    id="edit-prod-name"
                    type="text"
                    value={editProductForm.name}
                    onChange={(e) =>
                      handleEditProductFieldChange("name", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-prod-slug">Slug *</label>
                  <input
                    id="edit-prod-slug"
                    type="text"
                    value={editProductForm.slug}
                    onChange={(e) =>
                      handleEditProductFieldChange("slug", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-prod-price">Price (INR) *</label>
                  <input
                    id="edit-prod-price"
                    type="number"
                    step="0.01"
                    value={editProductForm.price}
                    onChange={(e) =>
                      handleEditProductFieldChange("price", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-prod-stock">Stock Quantity *</label>
                  <input
                    id="edit-prod-stock"
                    type="number"
                    value={editProductForm.stockQuantity}
                    onChange={(e) =>
                      handleEditProductFieldChange(
                        "stockQuantity",
                        e.target.value,
                      )
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-prod-mukhi">
                    Mukhi Count (Optional)
                  </label>
                  <input
                    id="edit-prod-mukhi"
                    type="number"
                    value={editProductForm.mukhiCount}
                    onChange={(e) =>
                      handleEditProductFieldChange("mukhiCount", e.target.value)
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-prod-origin">Origin (Optional)</label>
                  <input
                    id="edit-prod-origin"
                    type="text"
                    value={editProductForm.origin}
                    onChange={(e) =>
                      handleEditProductFieldChange("origin", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-prod-size">
                    Bead Size (mm) (Optional)
                  </label>
                  <input
                    id="edit-prod-size"
                    type="text"
                    value={editProductForm.beadSizeMm}
                    onChange={(e) =>
                      handleEditProductFieldChange("beadSizeMm", e.target.value)
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-prod-status">Status</label>
                  <select
                    id="edit-prod-status"
                    value={editProductForm.status}
                    onChange={(e) =>
                      handleEditProductFieldChange("status", e.target.value)
                    }
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                </div>
              </div>

              <div className="form-row certification-row">
                <div className="form-group checkbox-group">
                  <label
                    className="checkbox-label"
                    htmlFor="edit-prod-certified"
                  >
                    <input
                      id="edit-prod-certified"
                      type="checkbox"
                      checked={editProductForm.isCertified}
                      onChange={(e) =>
                        handleEditProductFieldChange(
                          "isCertified",
                          e.target.checked,
                        )
                      }
                    />
                    Is Lab Certified?
                  </label>
                </div>

                {editProductForm.isCertified && (
                  <div className="form-group">
                    <label htmlFor="edit-prod-cert-url">
                      Certification Report URL
                    </label>
                    <input
                      id="edit-prod-cert-url"
                      type="url"
                      value={editProductForm.certificationUrl}
                      onChange={(e) =>
                        handleEditProductFieldChange(
                          "certificationUrl",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                )}
              </div>

              {/* <div className="form-group full-width-group">
                <label htmlFor="edit-prod-desc">Product Description</label>
                <textarea
                  id="edit-prod-desc"
                  rows="4"
                  value={editProductForm.description}
                  onChange={(e) =>
                    handleEditProductFieldChange("description", e.target.value)
                  }
                />
              </div> */}
              <div className="form-group full-width-group blog-editor-group">
  <label>Product Description</label>

  <CKEditor
    key={editingProduct?.id || "edit-product"}
    editor={ClassicEditor}
    data={editProductForm.description || ""}
    config={{
      licenseKey: "GPL",

      plugins: [
        Essentials,
        Paragraph,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Heading,
        List,
        Link,
        BlockQuote,
        Table,
        TableToolbar,
        Alignment,
      ],

      toolbar: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "link",
        "bulletedList",
        "numberedList",
        "|",
        "alignment",
        "blockQuote",
        "insertTable",
      ],

      heading: {
        options: [
          {
            model: "paragraph",
            title: "Paragraph",
            class: "ck-heading_paragraph",
          },
          {
            model: "heading2",
            view: "h2",
            title: "Heading 2",
            class: "ck-heading_heading2",
          },
          {
            model: "heading3",
            view: "h3",
            title: "Heading 3",
            class: "ck-heading_heading3",
          },
          {
            model: "heading4",
            view: "h4",
            title: "Heading 4",
            class: "ck-heading_heading4",
          },
        ],
      },

      table: {
        contentToolbar: [
          "tableColumn",
          "tableRow",
          "mergeTableCells",
        ],
      },

      placeholder: "Write full product details and benefits...",
    }}
    onChange={(event, editor) => {
      handleEditProductFieldChange(
        "description",
        editor.getData()
      );
    }}
  />
</div>

              <div className="options-group-form-actions">
                <button type="submit" disabled={savingProduct}>
                  {savingProduct ? "Saving..." : "Save Changes"}
                </button>
                <button type="button" onClick={closeEditProduct}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedPodModal && (
        <div
          className="options-modal-overlay"
          onClick={() => setSelectedPodModal(null)}
        >
          <div
            className="options-modal"
            style={{ maxWidth: "650px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="options-modal-header">
              <h3>Product On Demand Request — {selectedPodModal.ref}</h3>
              <button
                className="options-modal-close"
                onClick={() => setSelectedPodModal(null)}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "14px",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <strong>Full Name:</strong>{" "}
                  <p style={{ margin: "2px 0", color: "#071A2F" }}>
                    {selectedPodModal.fullName}
                  </p>
                </div>
                <div>
                  <strong>Phone / WhatsApp:</strong>
                  <p style={{ margin: "2px 0" }}>
                    <a
                      href={`https://wa.me/${selectedPodModal.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${selectedPodModal.fullName}, regarding your Product On Demand request (${selectedPodModal.ref}):`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#25D366",
                        fontWeight: "bold",
                        textDecoration: "none",
                      }}
                    >
                      📞 {selectedPodModal.phone} (WhatsApp Chat)
                    </a>
                  </p>
                </div>
                <div>
                  <strong>Email:</strong>{" "}
                  <p style={{ margin: "2px 0" }}>
                    {selectedPodModal.email || "N/A"}
                  </p>
                </div>
                <div>
                  <strong>Product Category:</strong>{" "}
                  <p
                    style={{
                      margin: "2px 0",
                      color: "#C9A227",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedPodModal.category}
                  </p>
                </div>
                <div>
                  <strong>Metal / String Preference:</strong>{" "}
                  <p style={{ margin: "2px 0" }}>
                    {selectedPodModal.metalPreference || "N/A"}
                  </p>
                </div>
                <div>
                  <strong>Budget Range:</strong>{" "}
                  <p style={{ margin: "2px 0" }}>
                    ₹{selectedPodModal.budget || "N/A"}
                  </p>
                </div>
                <div>
                  <strong>Date of Birth:</strong>{" "}
                  <p style={{ margin: "2px 0" }}>
                    {selectedPodModal.dob || "N/A"}
                  </p>
                </div>
                <div>
                  <strong>Zodiac / Rashi:</strong>{" "}
                  <p style={{ margin: "2px 0" }}>
                    {selectedPodModal.rashi || "N/A"}
                  </p>
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <strong>Custom Requirements Description:</strong>
                <p
                  style={{
                    background: "#FAF7EE",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #DDD5BF",
                    whiteSpace: "pre-wrap",
                    marginTop: "4px",
                  }}
                >
                  {selectedPodModal.description || "No description provided."}
                </p>
              </div>

              {selectedPodModal.imageUrl && (
                <div style={{ textAlign: "center", marginTop: "16px" }}>
                  <strong>Uploaded Reference Image:</strong>
                  <div style={{ marginTop: "8px" }}>
                    <a
                      href={
                        selectedPodModal.imageUrl.startsWith("http")
                          ? selectedPodModal.imageUrl
                          : `${API.endsWith("/api/v1") ? API.slice(0, -7) : API}${selectedPodModal.imageUrl}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={
                          selectedPodModal.imageUrl.startsWith("http")
                            ? selectedPodModal.imageUrl
                            : `${API.endsWith("/api/v1") ? API.slice(0, -7) : API}${selectedPodModal.imageUrl}`
                        }
                        alt="Uploaded Reference"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          borderRadius: "8px",
                          border: "2px solid #C9A227",
                        }}
                      />
                    </a>
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "20px",
                }}
              >
                <button
                  className="delete-btn"
                  onClick={() => setSelectedPodModal(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
